import { sql } from "drizzle-orm";
import type { PurchaseClaimDetails } from "./purchaseClaims.js";
import {
  type AnyPgColumn,
  boolean,
  check,
  date,
  foreignKey,
  index,
  integer,
  jsonb,
  numeric,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";

/**
 * Conventions used throughout:
 *
 *  - UUID primary keys everywhere. No external system's identifier is ever a
 *    primary key — `notion_page_id` and `amelia_*_id` are plain references.
 *  - Every tenant-owned table carries `organisationId`. There is one
 *    organisation today; queries are scoped by middleware regardless, so
 *    adding a second never requires touching a table or a query.
 *  - Real foreign keys with explicit delete behaviour, so orphans are
 *    impossible rather than merely unlikely.
 *  - Timestamps are `timestamptz`. Amelia's naive Europe/London wall-clock
 *    strings are resolved to absolute instants at the adapter boundary and
 *    never stored naive.
 */

const id = () =>
  varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`);

const createdAt = () => timestamp("created_at", { withTimezone: true }).notNull().defaultNow();

// ── Tenancy ──────────────────────────────────────────────────────────────────

export const organisations = pgTable("organisations", {
  id: id(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  timezone: text("timezone").notNull().default("Europe/London"),
  createdAt: createdAt(),
});

const organisationId = () =>
  varchar("organisation_id")
    .notNull()
    .references(() => organisations.id, { onDelete: "restrict" });

// ── Identity ─────────────────────────────────────────────────────────────────

export const roleEnum = pgEnum("role", ["owner", "tutor", "student", "parent"]);

/** What is hanging off a practice session: bytes we hold, or a link we do not. */
export const attachmentKindEnum = pgEnum("attachment_kind", ["file", "youtube"]);

export type Role = (typeof roleEnum.enumValues)[number];

/**
 * An account. Identified by an email address, a username, or both.
 *
 * A username exists because a child often has no email of their own. The
 * booking provider's customer *is* an address — one row per address — so a
 * parent with two children is one customer there, and requiring an address
 * here would have forced siblings to share a login and therefore share a
 * practice log. A username costs one nullable column and keeps each child's
 * work their own.
 *
 * Exactly one of `email` and `username` is required; the check constraint
 * below is what makes that true rather than merely intended.
 */
export const users = pgTable(
  "users",
  {
    id: id(),
    organisationId: organisationId(),
    /** Null for an account that signs in by username. */
    email: text("email"),
    /** Null for an ordinary account. Lower-case; unique within the school. */
    username: text("username"),
    passwordHash: text("password_hash").notNull(),
    /**
     * Where a password reset goes when the account has no address of its own —
     * in practice a parent's. Without it a child who forgets their password
     * has no way back in that does not involve a tutor resetting it by hand.
     */
    recoveryEmail: text("recovery_email"),
    /**
     * When the address was proved to belong to whoever is using it.
     *
     * Null means it has not been, and such an account can do nothing at all —
     * it cannot sign in and it is linked to no student. That is the whole
     * point: a registration links an account to every student record carrying
     * the same address, so without this step, knowing a parent's address would
     * be enough to read their child's lesson notes.
     *
     * Deliberately no default. An account created by an owner — through the
     * CLI, or by adopting a tutor — is trusted because of who created it, and
     * says so explicitly. A path that forgets leaves somebody unable to sign
     * in, which is loud; a default of "confirmed" would let a path that forgets
     * hand out access quietly.
     *
     * Set for an account with no address of its own. A child signing in by
     * username has nothing to confirm.
     */
    emailConfirmedAt: timestamp("email_confirmed_at", { withTimezone: true }),
    lastLoginAt: timestamp("last_login_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [
    // Composite tenant references need a unique target even though id is globally unique.
    uniqueIndex("users_org_id_idx").on(t.organisationId, t.id),
    uniqueIndex("users_org_email_idx").on(t.organisationId, t.email),
    uniqueIndex("users_org_username_idx").on(t.organisationId, t.username),
    check(
      "users_identifier_present",
      sql`${t.email} is not null or ${t.username} is not null`,
    ),
  ],
);

/** A user may hold several roles — a tutor who is also the owner, say. */
export const userRoles = pgTable(
  "user_roles",
  {
    id: id(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    role: roleEnum("role").notNull(),
    createdAt: createdAt(),
  },
  (t) => [uniqueIndex("user_roles_user_role_idx").on(t.userId, t.role)],
);

/**
 * What somebody may do in the booking system, beyond their own lessons.
 *
 * Roles say who somebody is here; this says what they may reach on the other
 * side of the staff API. The two are deliberately not the same table. A role is
 * the whole shape of a person's relationship to the school and there are four
 * of them; a capability is one narrow permission out of a vocabulary FastAPI
 * owns, and the office needing `payments.manage` should not mean making
 * somebody an owner of the school to get it.
 *
 * **Owners can hold rows here too.** Temporary compatibility still treats an
 * owner as universally capable, but explicit rows are prepared and verified
 * before that behaviour is retired. These rows are the future authority source,
 * not a second role table.
 *
 * **The capability is text, not an enum.** The names belong to the integration
 * contract rather than to this database, so a capability Gary adds should be a
 * row somebody ticks, not a migration and a deploy. The safety this gives up is
 * bought back where it matters: a name is checked against the contract when it
 * is granted, and checked again when a token is minted, so a row that stops
 * meaning anything grants nothing rather than asserting a word FastAPI will
 * refuse.
 *
 * **Kept when somebody leaves.** Losing the `tutor` role is what stops these
 * counting — nothing non-staff is ever sent — so the rows can stay and be worth
 * exactly nothing until the day they are put back, which is one click and
 * should not also mean remembering what they used to be allowed to do.
 */
export const userCapabilities = pgTable(
  "user_capabilities",
  {
    id: id(),
    organisationId: organisationId(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    capability: text("capability").notNull(),
    /**
     * The owner who granted it. Null once that account is gone.
     *
     * Recorded for the same reason a DBS verification names who did it: a
     * permission to spend other people's money that nobody is named on is not
     * one anybody can ask about afterwards.
     */
    grantedByUserId: varchar("granted_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
  },
  (t) => [uniqueIndex("user_capabilities_user_capability_idx").on(t.userId, t.capability)],
);

/** Replaces the hardcoded ADMIN_EMAILS list, and links a tutor to Amelia. */
export const tutorProfiles = pgTable(
  "tutor_profiles",
  {
    id: id(),
    organisationId: organisationId(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    displayName: text("display_name").notNull(),
    /**
     * What colour this tutor's lessons are drawn in on the diary.
     *
     * A hex value chosen by an owner, or null for the ones nobody has got to
     * yet — which draw in the house accent like everything did before. Only
     * the hue and the saturation are read: the diary normalises how light the
     * block is so that a dark navy and a mid orange are equally legible, and
     * so that the same tutor's second instrument can be a shade of theirs
     * rather than a colour of its own.
     *
     * On the profile rather than in the front end because the eight people
     * here are managed in the browser; a palette compiled into the bundle
     * would mean a deploy every time somebody joined.
     */
    colour: text("colour"),
    ameliaProviderId: integer("amelia_provider_id"),
    /**
     * Whether they have agreed we may check them on the DBS Update Service.
     *
     * **Not the lawful basis for holding their DBS**, and the distinction is
     * the whole reason this column is worded as it is. The record is held on a
     * safeguarding obligation, not on consent — consent from somebody who
     * teaches for you is not freely given, and a basis they could withdraw is
     * one that would oblige us to delete a safeguarding record on request.
     * What this *is* is the thing the Update Service form demands every time:
     * a declaration that the checker is entitled to check **and has the
     * person's consent**. There is no way to make that declaration honestly
     * without having asked, and no way to evidence it without writing it down.
     *
     * On the profile rather than on `tutor_dbs` for two reasons. A tutor is
     * asked at signup, before any certificate exists, and `tutor_dbs` cannot
     * hold a row without a certificate number, an issue date and a level. And
     * consent is about the person and the standing arrangement rather than
     * about one document, so it correctly survives a certificate being
     * replaced — unlike `verified_at`, which must not.
     *
     * **Three states, deliberately.** Null is "never asked", true is "yes" and
     * false is "asked, and no" — which is also a withdrawal, since withdrawing
     * is just answering again. `students.email_consent` is the cautionary tale
     * here: `not null default false` means "said no" and "never asked" are the
     * same value, and roughly half that column is false purely because nobody
     * ever asked. A consent column that cannot tell those apart cannot answer
     * the only question anybody asks of it.
     */
    dbsCheckConsent: boolean("dbs_check_consent"),
    /** When that answer was given. Null exactly when the answer is null. */
    dbsCheckConsentAt: timestamp("dbs_check_consent_at", { withTimezone: true }),
    /**
     * When they stopped teaching here. Null while they still do.
     *
     * A column rather than a deleted row, because everything they wrote points
     * at this one. `lessons.tutor_id` is `on delete set null`, so removing the
     * row would quietly turn a year of their lesson notes into the
     * organisation's — unattributed, and visible only to owners. The person
     * leaves; the record of what they taught does not.
     *
     * What it actually stops is the `tutor` role, revoked in the same
     * transaction. This column is what makes the state legible afterwards —
     * "gone" as against "never got started" — and what keeps them out of the
     * lists you pick a current tutor from.
     */
    leftAt: timestamp("left_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("tutor_profiles_org_id_idx").on(t.organisationId, t.id),
    uniqueIndex("tutor_profiles_user_idx").on(t.userId),
    index("tutor_profiles_amelia_idx").on(t.organisationId, t.ameliaProviderId),
    /**
     * One colour, one tutor.
     *
     * An index rather than a check in the route, because two owners picking
     * the same colour at the same moment is exactly the case a read-then-write
     * loses — and a diary where two people are the same blue is worse than no
     * colours at all.
     *
     * Partial, on two counts. Null is "nobody has chosen", and several tutors
     * are allowed to be in that state at once. And somebody who has left keeps
     * their colour on the record while releasing it for whoever replaces them,
     * which is the behaviour that stops a school of eight running out after a
     * few years of turnover.
     */
    uniqueIndex("tutor_profiles_colour_idx")
      .on(t.organisationId, t.colour)
      .where(sql`${t.colour} is not null and ${t.leftAt} is null`),
  ],
);

/**
 * Immutable evidence for sensitive Hub-domain decisions and mutations.
 *
 * This is an accountability record, not a booking, payment, credit, or
 * reconciliation ledger. The authoritative business records remain in their
 * own tables; these rows answer who asked, under which accepted authority,
 * and what happened next.
 */
export const domainAuditEvents = pgTable(
  "domain_audit_events",
  {
    id: id(),
    organisationId: organisationId(),
    actorUserId: varchar("actor_user_id").references(() => users.id, {
      onDelete: "restrict",
    }),
    actorKind: text("actor_kind").notNull(),
    authenticationSource: text("authentication_source").notNull(),
    requestId: varchar("request_id"),
    domain: text("domain").notNull(),
    operation: text("operation").notNull(),
    resourceType: text("resource_type").notNull(),
    resourceId: varchar("resource_id"),
    allowed: boolean("allowed").notNull(),
    decisionReason: text("decision_reason").notNull(),
    authorityBasis: jsonb("authority_basis").notNull().default({}),
    eventType: text("event_type").notNull(),
    decisionEventId: varchar("decision_event_id").references(
      (): AnyPgColumn => domainAuditEvents.id,
      { onDelete: "restrict" },
    ),
    outcome: text("outcome").notNull(),
    outcomeDetail: text("outcome_detail"),
    idempotencyKey: varchar("idempotency_key"),
    relatedRecordType: text("related_record_type"),
    relatedRecordId: varchar("related_record_id"),
    beforeState: jsonb("before_state"),
    afterState: jsonb("after_state"),
    delta: jsonb("delta"),
    serviceContext: jsonb("service_context").notNull().default({}),
    createdAt: createdAt(),
  },
  (t) => [
    index("domain_audit_events_org_created_idx").on(t.organisationId, t.createdAt),
    index("domain_audit_events_decision_idx").on(t.decisionEventId),
    index("domain_audit_events_resource_idx").on(t.resourceType, t.resourceId),
  ],
);

/** Which check was run. A music school teaching children needs the last one. */
export const dbsLevelEnum = pgEnum("dbs_level", [
  "basic",
  "standard",
  "enhanced",
  "enhanced-barred",
]);

/** What a check of the Update Service actually said. */
export const dbsCheckOutcomeEnum = pgEnum("dbs_check_outcome", [
  /** Certificate current, no new information. */
  "clear",
  /** The service says new information has come to light. Act on it off-portal. */
  "new-information",
  /** The number or details did not match anything. */
  "not-found",
  /** They are no longer subscribed, so there is nothing to check against. */
  "lapsed",
]);

/**
 * A tutor's DBS certificate, as a reference rather than as a document.
 *
 * **Its own table, deliberately.** This is criminal-offence data — Article 10
 * of the UK GDPR — and putting it on `tutor_profiles` would drag it into every
 * staff query, every diary join and every `select *` in the codebase. Here it
 * is reached only by the two places entitled to it, and could be purged with
 * one `delete` if the school ever had to.
 *
 * **What is not here is the point.** No scan, no copy of the certificate, and
 * nothing it disclosed. DBS's own guidance is not to retain certificate copies
 * beyond six months, and the school's lawful need is to know a valid check
 * exists — not what it said. The number and the issue date are kept because
 * they are what a person needs in order to run the Update Service check; the
 * result of running it goes in `tutor_dbs_checks` as an outcome, never as
 * detail.
 *
 * **Entering it is a claim; `verifiedAt` is the proof.** A tutor typing their
 * own certificate number has told us a number. Somebody has to have seen the
 * original document, and that is a different act by a different person — the
 * same distinction the invitation flow makes about an address.
 */
export const tutorDbs = pgTable(
  "tutor_dbs",
  {
    id: id(),
    organisationId: organisationId(),
    tutorProfileId: varchar("tutor_profile_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "cascade" }),

    /** The twelve-digit certificate number. Stored whole: it is the lookup key. */
    certificateNumber: text("certificate_number").notNull(),
    /**
     * Dates rather than timestamps, and that is not laziness.
     *
     * A certificate is issued on a day, not at an instant. Stored as
     * `timestamptz` it would land at midnight UTC and read as the day before
     * to anyone in a negative offset — a renewal that silently lapses a day
     * early is exactly the sort of bug nobody finds until it matters.
     */
    issuedOn: date("issued_on").notNull(),
    level: dbsLevelEnum("level").notNull(),

    /**
     * The Update Service is what makes a certificate mean anything after the
     * day it was printed: without it, this is a snapshot of a person's record
     * on one morning, however long ago.
     */
    updateServiceSubscribed: boolean("update_service_subscribed").notNull().default(false),
    /** The annual renewal. Letting it lapse means buying a whole new DBS. */
    updateServiceRenewsOn: date("update_service_renews_on"),

    /** When somebody saw the original certificate. Null until they have. */
    verifiedAt: timestamp("verified_at", { withTimezone: true }),
    /** Null only if that owner's account has since been deleted. */
    verifiedByUserId: varchar("verified_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),

    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("tutor_dbs_profile_idx").on(t.tutorProfileId),
    index("tutor_dbs_org_idx").on(t.organisationId),
  ],
);

/**
 * Every time somebody checked the Update Service, and what it said.
 *
 * The portal cannot run this check: the Update Service is a web form, and it
 * asks for a declaration that the person running it is entitled to and has
 * consent. So the monthly job names who needs checking, a person does it, and
 * this is where the answer lands.
 *
 * It is the whole compliance artefact. "Our system says everyone is valid" is
 * worth nothing to an inspector; "checked on 1 September by this person, clear"
 * is the thing being asked for. Rows are never edited or removed — a log that
 * can be tidied is not a log.
 */
export const tutorDbsChecks = pgTable(
  "tutor_dbs_checks",
  {
    id: id(),
    organisationId: organisationId(),
    tutorProfileId: varchar("tutor_profile_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "cascade" }),
    checkedAt: createdAt(),
    checkedByUserId: varchar("checked_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    outcome: dbsCheckOutcomeEnum("outcome").notNull(),
    /**
     * Anything worth remembering about the check itself — never what the
     * certificate disclosed. "Rang the DBS line, number retyped" is the sort
     * of thing this is for.
     */
    note: text("note"),
  },
  (t) => [index("tutor_dbs_checks_profile_idx").on(t.tutorProfileId, t.checkedAt)],
);

// ── Students ─────────────────────────────────────────────────────────────────

export const students = pgTable(
  "students",
  {
    id: id(),
    organisationId: organisationId(),
    /** Null until the student registers and is verified. */
    userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
    tutorId: varchar("tutor_id").references(() => tutorProfiles.id, { onDelete: "set null" }),

    fullName: text("full_name").notNull(),
    studentEmail: text("student_email"),
    parentEmail: text("parent_email"),
    secondaryEmail: text("secondary_email"),
    parentName: text("parent_name"),
    parentPhone: text("parent_phone"),
    isUnder18: boolean("is_under_18").notNull().default(false),

    school: text("school"),
    formClass: text("form_class"),
    instrument: text("instrument"),
    lessonLength: text("lesson_length"),
    handedness: text("handedness"),
    hasOwnInstrument: boolean("has_own_instrument"),
    previousExperience: text("previous_experience"),
    emailConsent: boolean("email_consent").notNull().default(false),

    status: text("status").notNull().default("active"),
    calendarFeedToken: text("calendar_feed_token").unique(),

    /**
     * Amelia's customer id. Stable and 1:1 with email, so it identifies the
     * *booker* unambiguously — but a parent booking for two children is one
     * customer, so it does not by itself identify the student. Sibling
     * attribution comes from the booking's custom fields.
     */
    ameliaCustomerId: integer("amelia_customer_id"),
    /** Self-service enrolment cannot inherit unrelated history through name/email matching. */
    automaticAttribution: boolean("automatic_attribution").notNull().default(true),
    /** A sync-created record has no account access until a claim/invitation verifies it. */
    autoCreated: boolean("auto_created").notNull().default(false),
    /**
     * Who pays for this child's lessons, where somebody has said so.
     *
     * `family`, `school`, `self` — or null, which is not "unknown" but "work it
     * out", and is what all 176 existing students are. The rule is in
     * `apps/hub/src/booking/identity.ts`, and it reaches the same answer as
     * before for everybody who has a parent's address on file.
     *
     * It exists because `school` above is *which school a child attends*, which
     * most private students also have. Reading it as "the school pays" would
     * bill a school for Saturday-morning guitar lessons the parents booked, so
     * a school payer is something stated rather than inferred from that field.
     */
    billingPayer: text("billing_payer"),
    /**
     * The tutor who added this record from a lesson, until an owner has looked
     * at it.
     *
     * A tutor may create a student from the lesson they are teaching — that is
     * how a sibling booked on one card gets a record of their own without
     * waiting for somebody at a desk. What the tutor supplies is a name and
     * nothing else; the addresses come from the booking. But a name heard in a
     * room is not always the name that should be on a record — "Nate" for
     * Nathaniel, a surname guessed at — and that name then appears on every
     * list in the school.
     *
     * So the record says who made it, and the queue shows the ones nobody has
     * checked. Cleared by an owner confirming it reads right, which is why this
     * is a tutor reference rather than a boolean: "added by Paul" is what makes
     * the question answerable without opening the lesson it came from.
     */
    addedByTutorId: varchar("added_by_tutor_id").references(() => tutorProfiles.id, {
      onDelete: "set null",
    }),
    /** Legacy reference only. Nothing addresses a student by this. */
    notionPageId: text("notion_page_id"),
    /** The old system's free-text student reference, kept so nothing is lost. */
    legacyStudentRef: text("legacy_student_ref"),

    createdAt: createdAt(),
    lastLoginAt: timestamp("last_login_at", { withTimezone: true }),
  },
  (t) => [
    index("students_org_idx").on(t.organisationId),
    uniqueIndex("students_org_id_idx").on(t.organisationId, t.id),
    index("students_user_idx").on(t.userId),
    index("students_tutor_idx").on(t.tutorId),
    index("students_amelia_customer_idx").on(t.organisationId, t.ameliaCustomerId),
  ],
);

/**
 * An owner-managed pupil relationship for the Hub-native School Day domain.
 *
 * This is intentionally not derived from `students.school`: that free-text
 * field records where a child attends and is also used by private pupils.
 * Nor does it point at `schools`, whose rows are payer/customer configuration.
 * A School Day membership answers the different, explicit question: may this
 * child be planned into this school's local teaching timetable, with this
 * tutor and service?
 */
export const schoolDayMemberships = pgTable(
  "school_day_memberships",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    /** Canonical display name used by the School Day planner. */
    schoolName: text("school_name").notNull(),
    /** Case-insensitive key for membership and roster resolution. */
    schoolKey: text("school_key").notNull(),
    /** The current School Day teacher, never inferred from private lessons. */
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),
    /** The service this tutor is currently authorised to teach at the school. */
    serviceName: text("service_name").notNull(),
    /**
     * What one of this pupil's school lessons costs their family, in pounds.
     *
     * Here rather than on a rate card because the rate belongs to the teaching
     * relationship: it starts when the pupil starts, ends when the membership
     * ends, and a pupil who moves school or service is re-priced by the same
     * edit that moves them. Null means "not priced yet", which is a real state
     * — a membership can exist before anybody has agreed a figure — and it is
     * the one thing that stops a pupil being invoiced.
     *
     * Amelia never sees this. `appointment_bookings.price` is Amelia's number
     * for a private lesson and is deliberately null on a school lesson; this
     * is the school side's own, and the two never meet.
     */
    lessonRate: numeric("lesson_rate", { precision: 10, scale: 2 }),
    /** Ending a relationship retains its provenance while removing access. */
    active: boolean("active").notNull().default(true),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("school_day_memberships_org_student_idx").on(t.organisationId, t.studentId),
    index("school_day_memberships_roster_idx").on(t.organisationId, t.schoolKey, t.active),
    index("school_day_memberships_tutor_idx").on(t.organisationId, t.tutorId, t.active),
  ],
);

/**
 * The private-domain teaching relationship. School Day membership is a
 * separate bounded context and must never be represented by this table.
 */
export const teachingAssignmentStatusEnum = pgEnum("teaching_assignment_status", [
  "pending-review",
  "active",
  "ended",
  "rejected",
]);

export const teachingAssignmentSourceEnum = pgEnum("teaching_assignment_source", [
  "owner-assignment",
  "tutor-enrolment",
  "migration",
  "system",
]);

export const teachingAssignmentCandidateConfidenceEnum = pgEnum(
  "teaching_assignment_candidate_confidence",
  ["high", "medium", "low", "ambiguous"],
);

export const teachingAssignmentCandidateStatusEnum = pgEnum(
  "teaching_assignment_candidate_status",
  ["pending", "approved", "rejected"],
);

export type TeachingAssignmentStatus = (typeof teachingAssignmentStatusEnum.enumValues)[number];
export type TeachingAssignmentSource = (typeof teachingAssignmentSourceEnum.enumValues)[number];

/**
 * Canonical, time-bounded teaching authority for the private domain.
 *
 * `students.tutorId`, appointment history, payer data and Amelia customer
 * history remain evidence during migration; they are deliberately not
 * foreign keys on this relationship. An assignment is an immutable fact of
 * who was authorised to teach which service, with lifecycle changes recorded
 * by ending the row rather than rewriting its history.
 */
export const teachingAssignments = pgTable(
  "teaching_assignments",
  {
    id: id(),
    organisationId: organisationId(),
    /** A3 is private-domain only. School Day has its own membership model. */
    domain: text("domain").notNull().default("private"),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "restrict" }),
    tutorProfileId: varchar("tutor_profile_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),
    /** Verified Amelia provider identity captured when the relation is approved. */
    verifiedProviderId: integer("verified_provider_id").notNull(),
    /** Service scope may be represented by Amelia id, name, or instrument. */
    serviceId: integer("service_id"),
    serviceName: text("service_name"),
    instrument: text("instrument"),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }),
    status: teachingAssignmentStatusEnum("status").notNull().default("pending-review"),
    source: teachingAssignmentSourceEnum("source").notNull(),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdReason: text("created_reason").notNull(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    endedByUserId: varchar("ended_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    endedReason: text("ended_reason"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    /** Optimistic concurrency token; lifecycle updates must compare-and-swap. */
    version: integer("version").notNull().default(1),
  },
  (t) => [
    check("teaching_assignments_private_domain", sql`${t.domain} = 'private'`),
    check(
      "teaching_assignments_service_scope",
      sql`${t.serviceId} is not null or ${t.serviceName} is not null or ${t.instrument} is not null`,
    ),
    check(
      "teaching_assignments_date_order",
      sql`${t.endsAt} is null or ${t.endsAt} > ${t.startsAt}`,
    ),
    check("teaching_assignments_version_positive", sql`${t.version} > 0`),
    check(
      "teaching_assignments_ended_metadata",
      sql`${t.status} <> 'ended' or (${t.endedAt} is not null and ${t.endedReason} is not null)`,
    ),
    uniqueIndex("teaching_assignments_identity_idx").on(
      t.organisationId,
      t.domain,
      t.studentId,
      t.tutorProfileId,
      t.verifiedProviderId,
      t.serviceId,
      t.serviceName,
      t.instrument,
      t.startsAt,
    ),
    index("teaching_assignments_active_lookup_idx").on(
      t.organisationId,
      t.domain,
      t.studentId,
      t.tutorProfileId,
      t.status,
      t.startsAt,
    ),
    index("teaching_assignments_provider_lookup_idx").on(
      t.organisationId,
      t.verifiedProviderId,
      t.status,
      t.startsAt,
    ),
  ],
);

/**
 * Review queue produced by the read-only migration inventory. Candidates are
 * evidence, not authority: no row becomes an assignment until an owner
 * explicitly approves it.
 */
export const teachingAssignmentCandidates = pgTable(
  "teaching_assignment_candidates",
  {
    id: id(),
    organisationId: organisationId(),
    domain: text("domain").notNull().default("private"),
    /** Stable, deterministic key makes repeated inventory runs idempotent. */
    candidateKey: text("candidate_key").notNull(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "restrict" }),
    tutorProfileId: varchar("tutor_profile_id").references(() => tutorProfiles.id, {
      onDelete: "restrict",
    }),
    verifiedProviderId: integer("verified_provider_id"),
    serviceId: integer("service_id"),
    serviceName: text("service_name"),
    instrument: text("instrument"),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }),
    confidence: teachingAssignmentCandidateConfidenceEnum("confidence").notNull(),
    evidence: jsonb("evidence").notNull(),
    source: text("source").notNull().default("migration"),
    status: teachingAssignmentCandidateStatusEnum("status").notNull().default("pending"),
    reviewedByUserId: varchar("reviewed_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    reviewReason: text("review_reason"),
    /**
     * True when a clear-cut candidate (a recurring booking that already
     * matches an unambiguous tutor and service) was activated by the
     * inventory run itself, with no owner decision. Raw booking history has
     * no concept of "this stopped", which is the one thing a person still
     * has to supply — but confirming a relationship the data already shows
     * plainly is not that, and treating it as a queue item nobody clears is
     * how a real fix turns into a chore nobody does.
     */
    autoActivated: boolean("auto_activated").notNull().default(false),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    check("teaching_assignment_candidates_private_domain", sql`${t.domain} = 'private'`),
    check(
      "teaching_assignment_candidates_date_order",
      sql`${t.endsAt} is null or ${t.endsAt} > ${t.startsAt}`,
    ),
    check("teaching_assignment_candidates_version_positive", sql`${t.version} > 0`),
    check(
      "teaching_assignment_candidates_review_metadata",
      sql`${t.status} = 'pending' or (${t.reviewedAt} is not null and ${t.reviewReason} is not null and (${t.reviewedByUserId} is not null or ${t.autoActivated} = true))`,
    ),
    uniqueIndex("teaching_assignment_candidates_key_idx").on(t.organisationId, t.candidateKey),
    index("teaching_assignment_candidates_review_idx").on(
      t.organisationId,
      t.status,
      t.confidence,
      t.createdAt,
    ),
    index("teaching_assignment_candidates_student_idx").on(
      t.organisationId,
      t.studentId,
      t.status,
    ),
  ],
);

/**
 * A school, as somebody who pays for lessons.
 *
 * Not a directory of schools — `students.school` is free text and stays that
 * way, because it means "which school this child attends" and is filled in for
 * private students too. A row here exists for a school the music school *bills*,
 * and it exists because the booking system needs a payer.
 *
 * The reasoning matters, because "why is a school a customer" is a fair
 * question. An Amelia customer is a **booker, not a student**: a parent with two
 * children is one customer, and the student name in custom field 4 is what tells
 * the siblings apart. A school is exactly that shape — one payer, thirty
 * children, told apart by the same field. So a school pupil needs no new
 * concept in Amelia, only a payer, and this is where the Hub keeps the one it
 * gave them.
 *
 * `ameliaCustomerId` is filled in by FastAPI reporting back through the
 * link-back endpoint after it has created the customer, never by this side
 * guessing. Null means nobody has been booked here yet.
 */
export const schools = pgTable(
  "schools",
  {
    id: id(),
    organisationId: organisationId(),
    /** As written on a student's record, for a human to recognise. */
    name: text("name").notNull(),
    /**
     * The same name with case and spacing taken out, which is what a student's
     * free-text `school` is matched against. Two rows for "St Mary's" and
     * "st marys " would be two payers for one school, and one of them would
     * quietly hold the credits.
     */
    nameKey: text("name_key").notNull(),

    /**
     * Both halves, stored rather than split off a single field.
     *
     * FastAPI requires a non-empty first and last name before it will call
     * Amelia, and splitting "St Mary's Primary" at the last space gives
     * "St Mary's" and "Primary", which is what the school would then be called
     * on every confirmation email it receives. So somebody types both.
     */
    contactFirstName: text("contact_first_name").notNull(),
    contactLastName: text("contact_last_name").notNull(),
    /** Amelia customers are one-to-one with email; this is what finds theirs. */
    contactEmail: text("contact_email").notNull(),
    contactPhone: text("contact_phone"),

    ameliaCustomerId: integer("amelia_customer_id"),
    createdAt: createdAt(),
  },
  (t) => [uniqueIndex("schools_org_name_key_idx").on(t.organisationId, t.nameKey)],
);

// ── School bounded context ─────────────────────────────────────────────────

/** Lifecycle shared by School-domain identities and records. */
export const schoolDomainStatusEnum = pgEnum("school_domain_status", [
  "pending",
  "active",
  "ended",
  "archived",
]);

export const schoolMigrationCandidateConfidenceEnum = pgEnum(
  "school_migration_candidate_confidence",
  ["high", "medium", "low", "ambiguous"],
);

export const schoolMigrationCandidateStatusEnum = pgEnum("school_migration_candidate_status", [
  "pending",
  "approved",
  "rejected",
]);

export const schoolMigrationRunModeEnum = pgEnum("school_migration_run_mode", [
  "inventory",
  "copy",
  "shadow",
]);

export const schoolMigrationRunStatusEnum = pgEnum("school_migration_run_status", [
  "running",
  "completed",
  "failed",
]);

export const schoolOperationStatusEnum = pgEnum("school_operation_status", [
  "pending",
  "completed",
  "failed",
]);

export const schoolOutboxStatusEnum = pgEnum("school_outbox_status", [
  "pending",
  "processing",
  "published",
  "failed",
]);

export const schoolAvailabilityStatusEnum = pgEnum("school_availability_status", [
  "pending",
  "applied",
  "failed",
  "removed",
]);

export const schoolIdentityBridgeStatusEnum = pgEnum("school_identity_bridge_status", [
  "pending-review",
  "approved",
  "rejected",
  "retired",
]);

export const schoolAssignmentStatusEnum = pgEnum("school_assignment_status", [
  "pending-review",
  "active",
  "ended",
  "rejected",
]);

/**
 * A School pupil is a School-domain identity, not a projection of `students`.
 * `sourceKind`/`sourceId` are migration evidence only; they are deliberately
 * plain values rather than a foreign key into the private domain. The bridge
 * service is the only code allowed to interpret them.
 */
export const schoolPupils = pgTable(
  "school_pupils",
  {
    id: id(),
    organisationId: organisationId(),
    schoolKey: text("school_key").notNull(),
    firstName: text("first_name").notNull(),
    lastName: text("last_name").notNull(),
    preferredName: text("preferred_name"),
    email: text("email"),
    phone: text("phone"),
    status: schoolDomainStatusEnum("status").notNull().default("pending"),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceChecksum: text("source_checksum"),
    metadata: jsonb("metadata"),
    createdBySubject: text("created_by_subject"),
    updatedBySubject: text("updated_by_subject"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_pupils_org_school_idx").on(t.organisationId, t.schoolKey, t.status),
    uniqueIndex("school_pupils_org_id_idx").on(t.organisationId, t.id),
    uniqueIndex("school_pupils_org_source_idx").on(
      t.organisationId,
      t.sourceKind,
      t.sourceId,
    ),
    check("school_pupils_version_positive", sql`${t.version} > 0`),
  ],
);

/** School payer identity; never reuse or join the private `schools` payer. */
export const schoolPayers = pgTable(
  "school_payers",
  {
    id: id(),
    organisationId: organisationId(),
    schoolKey: text("school_key").notNull(),
    displayName: text("display_name").notNull(),
    contactFirstName: text("contact_first_name"),
    contactLastName: text("contact_last_name"),
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),
    status: schoolDomainStatusEnum("status").notNull().default("pending"),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceChecksum: text("source_checksum"),
    metadata: jsonb("metadata"),
    createdBySubject: text("created_by_subject"),
    updatedBySubject: text("updated_by_subject"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_payers_org_school_idx").on(t.organisationId, t.schoolKey, t.status),
    uniqueIndex("school_payers_org_school_key_idx").on(t.organisationId, t.schoolKey),
    uniqueIndex("school_payers_org_source_idx").on(t.organisationId, t.sourceKind, t.sourceId),
    check("school_payers_version_positive", sql`${t.version} > 0`),
  ],
);

/** School session/appointment, independent of the private appointments table. */
export const schoolSessions = pgTable(
  "school_sessions",
  {
    id: id(),
    organisationId: organisationId(),
    schoolKey: text("school_key").notNull(),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    status: text("status").notNull(),
    availabilityStatus: schoolAvailabilityStatusEnum("availability_status")
      .notNull()
      .default("pending"),
    availabilityError: text("availability_error"),
    availabilityUpdatedAt: timestamp("availability_updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    verifiedProviderId: integer("verified_provider_id").notNull(),
    serviceId: integer("service_id"),
    serviceName: text("service_name"),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceExternalId: text("source_external_id"),
    sourceChecksum: text("source_checksum"),
    metadata: jsonb("metadata"),
    createdBySubject: text("created_by_subject"),
    updatedBySubject: text("updated_by_subject"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_sessions_org_schedule_idx").on(t.organisationId, t.startsAt, t.endsAt),
    index("school_sessions_org_school_idx").on(t.organisationId, t.schoolKey, t.startsAt),
    uniqueIndex("school_sessions_org_source_idx").on(
      t.organisationId,
      t.sourceKind,
      t.sourceId,
    ),
    check("school_sessions_date_order", sql`${t.endsAt} > ${t.startsAt}`),
    check("school_sessions_version_positive", sql`${t.version} > 0`),
  ],
);

export const schoolSessionParticipantKindEnum = pgEnum("school_session_participant_kind", [
  "pupil",
  "payer",
]);

/** Participants point only at School identities; no private student FK exists. */
export const schoolSessionParticipants = pgTable(
  "school_session_participants",
  {
    id: id(),
    sessionId: varchar("session_id")
      .notNull()
      .references(() => schoolSessions.id, { onDelete: "cascade" }),
    pupilId: varchar("pupil_id").references(() => schoolPupils.id, { onDelete: "restrict" }),
    payerId: varchar("payer_id").references(() => schoolPayers.id, { onDelete: "restrict" }),
    kind: schoolSessionParticipantKindEnum("kind").notNull(),
    status: text("status").notNull().default("active"),
    sourceId: text("source_id"),
    sourceChecksum: text("source_checksum"),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("school_session_participants_pupil_idx").on(t.sessionId, t.pupilId, t.kind),
    uniqueIndex("school_session_participants_payer_idx").on(t.sessionId, t.payerId, t.kind),
    check(
      "school_session_participants_identity_check",
      sql`(${t.kind} = 'pupil' and ${t.pupilId} is not null and ${t.payerId} is null) or (${t.kind} = 'payer' and ${t.payerId} is not null and ${t.pupilId} is null)`,
    ),
  ],
);

/** School tutor/service relationship, separate from private A3 assignments. */
export const schoolAssignments = pgTable(
  "school_assignments",
  {
    id: id(),
    organisationId: organisationId(),
    schoolPupilId: varchar("school_pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    /** Opaque School subject; no join to a private user/tutor is implicit. */
    schoolTutorSubject: text("school_tutor_subject").notNull(),
    verifiedProviderId: integer("verified_provider_id").notNull(),
    serviceId: integer("service_id"),
    serviceName: text("service_name"),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }),
    status: schoolAssignmentStatusEnum("status").notNull().default("pending-review"),
    source: text("source").notNull(),
    createdBySubject: text("created_by_subject"),
    createdReason: text("created_reason").notNull(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    endedBySubject: text("ended_by_subject"),
    endedReason: text("ended_reason"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_assignments_active_idx").on(
      t.organisationId,
      t.schoolPupilId,
      t.status,
      t.startsAt,
    ),
    check("school_assignments_date_order", sql`${t.endsAt} is null or ${t.endsAt} > ${t.startsAt}`),
    check("school_assignments_version_positive", sql`${t.version} > 0`),
    check(
      "school_assignments_ended_metadata",
      sql`${t.status} <> 'ended' or (${t.endedAt} is not null and ${t.endedReason} is not null)`,
    ),
  ],
);

export const schoolNotes = pgTable(
  "school_notes",
  {
    id: id(),
    organisationId: organisationId(),
    sessionId: varchar("session_id")
      .notNull()
      .references(() => schoolSessions.id, { onDelete: "restrict" }),
    pupilId: varchar("pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    authorSubject: text("author_subject").notNull(),
    content: text("content").notNull(),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceChecksum: text("source_checksum"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    index("school_notes_org_pupil_idx").on(t.organisationId, t.pupilId, t.createdAt),
    index("school_notes_session_idx").on(t.sessionId),
    uniqueIndex("school_notes_org_source_idx").on(t.organisationId, t.sourceKind, t.sourceId),
    check("school_notes_version_positive", sql`${t.version} > 0`),
  ],
);

export const schoolAttendance = pgTable(
  "school_attendance",
  {
    id: id(),
    organisationId: organisationId(),
    sessionId: varchar("session_id")
      .notNull()
      .references(() => schoolSessions.id, { onDelete: "restrict" }),
    pupilId: varchar("pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    attendanceStatus: text("attendance_status").notNull(),
    recordedBySubject: text("recorded_by_subject").notNull(),
    recordedAt: timestamp("recorded_at", { withTimezone: true }).notNull().defaultNow(),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    sourceChecksum: text("source_checksum"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("school_attendance_session_pupil_idx").on(t.sessionId, t.pupilId),
    uniqueIndex("school_attendance_org_source_idx").on(t.organisationId, t.sourceKind, t.sourceId),
    index("school_attendance_org_pupil_idx").on(t.organisationId, t.pupilId, t.recordedAt),
  ],
);

/** A School-only report or export snapshot. */
export const schoolReports = pgTable(
  "school_reports",
  {
    id: id(),
    organisationId: organisationId(),
    schoolKey: text("school_key").notNull(),
    reportKind: text("report_kind").notNull(),
    periodStartsAt: timestamp("period_starts_at", { withTimezone: true }),
    periodEndsAt: timestamp("period_ends_at", { withTimezone: true }),
    payload: jsonb("payload").notNull(),
    sourceRunId: varchar("source_run_id"),
    checksum: text("checksum").notNull(),
    createdBySubject: text("created_by_subject").notNull(),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("school_reports_snapshot_idx").on(
      t.organisationId,
      t.schoolKey,
      t.reportKind,
      t.periodStartsAt,
      t.periodEndsAt,
      t.checksum,
    ),
    index("school_reports_org_period_idx").on(t.organisationId, t.schoolKey, t.periodStartsAt),
  ],
);

/**
 * Restricted bridge between a School identity and a private identity. This
 * table is deliberately not referenced by general-domain tables. Only the
 * migration/bridge service may read it; no route should expose either ID.
 */
export const schoolIdentityBridges = pgTable(
  "school_identity_bridges",
  {
    id: id(),
    organisationId: organisationId(),
    schoolPupilId: varchar("school_pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    privateIdentityKind: text("private_identity_kind").notNull(),
    privateIdentityId: text("private_identity_id").notNull(),
    status: schoolIdentityBridgeStatusEnum("status").notNull().default("pending-review"),
    confidence: schoolMigrationCandidateConfidenceEnum("confidence").notNull(),
    mappingReason: text("mapping_reason").notNull(),
    approvedBySubject: text("approved_by_subject"),
    approvedAt: timestamp("approved_at", { withTimezone: true }),
    sourceChecksum: text("source_checksum"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    uniqueIndex("school_identity_bridges_pair_idx").on(
      t.organisationId,
      t.schoolPupilId,
      t.privateIdentityKind,
      t.privateIdentityId,
    ),
    index("school_identity_bridges_review_idx").on(t.organisationId, t.status, t.confidence),
    check("school_identity_bridges_version_positive", sql`${t.version} > 0`),
    check(
      "school_identity_bridges_approval_metadata",
      sql`${t.status} not in ('approved', 'retired') or (${t.approvedBySubject} is not null and ${t.approvedAt} is not null)`,
    ),
  ],
);

/** Idempotent inventory/copy/shadow run envelope and checksum evidence. */
export const schoolMigrationRuns = pgTable(
  "school_migration_runs",
  {
    id: id(),
    organisationId: organisationId(),
    mode: schoolMigrationRunModeEnum("mode").notNull(),
    status: schoolMigrationRunStatusEnum("status").notNull().default("running"),
    sourceWindow: jsonb("source_window"),
    counts: jsonb("counts").notNull().default({}),
    checksums: jsonb("checksums").notNull().default({}),
    createdBySubject: text("created_by_subject").notNull(),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [
    index("school_migration_runs_org_mode_idx").on(t.organisationId, t.mode, t.createdAt),
    check(
      "school_migration_runs_completion_metadata",
      sql`${t.status} = 'running' or ${t.completedAt} is not null`,
    ),
  ],
);

/**
 * Review queue for migration evidence. Candidates never become School
 * authority automatically, even when their confidence is high.
 */
export const schoolMigrationCandidates = pgTable(
  "school_migration_candidates",
  {
    id: id(),
    organisationId: organisationId(),
    candidateKey: text("candidate_key").notNull(),
    sourceKind: text("source_kind").notNull(),
    sourceId: text("source_id").notNull(),
    schoolKey: text("school_key").notNull(),
    confidence: schoolMigrationCandidateConfidenceEnum("confidence").notNull(),
    evidence: jsonb("evidence").notNull(),
    sourceChecksum: text("source_checksum").notNull(),
    status: schoolMigrationCandidateStatusEnum("status").notNull().default("pending"),
    reviewedBySubject: text("reviewed_by_subject"),
    reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
    reviewReason: text("review_reason"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    version: integer("version").notNull().default(1),
  },
  (t) => [
    uniqueIndex("school_migration_candidates_key_idx").on(t.organisationId, t.candidateKey),
    index("school_migration_candidates_review_idx").on(
      t.organisationId,
      t.status,
      t.confidence,
      t.createdAt,
    ),
    check("school_migration_candidates_version_positive", sql`${t.version} > 0`),
    check(
      "school_migration_candidates_review_metadata",
      sql`${t.status} = 'pending' or (${t.reviewedBySubject} is not null and ${t.reviewedAt} is not null and ${t.reviewReason} is not null)`,
    ),
  ],
);

/**
 * Idempotent School-domain operation envelope. It is not a financial ledger:
 * it only makes a School write replayable and gives the outbox a durable
 * correlation key.
 */
export const schoolOperations = pgTable(
  "school_operations",
  {
    id: id(),
    organisationId: organisationId(),
    idempotencyKey: text("idempotency_key").notNull(),
    operation: text("operation").notNull(),
    requestFingerprint: text("request_fingerprint").notNull(),
    status: schoolOperationStatusEnum("status").notNull().default("pending"),
    aggregateId: varchar("aggregate_id"),
    request: jsonb("request").notNull(),
    response: jsonb("response"),
    errorCode: text("error_code"),
    actorSubject: text("actor_subject").notNull(),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    completedAt: timestamp("completed_at", { withTimezone: true }),
  },
  (t) => [
    uniqueIndex("school_operations_org_key_idx").on(t.organisationId, t.idempotencyKey),
    index("school_operations_org_status_idx").on(t.organisationId, t.status, t.createdAt),
    check(
      "school_operations_completion_metadata",
      sql`${t.status} = 'pending' or ${t.completedAt} is not null`,
    ),
  ],
);

/** Outbox event emitted in the same transaction as each School mutation. */
export const schoolOutboxEvents = pgTable(
  "school_outbox_events",
  {
    id: id(),
    organisationId: organisationId(),
    operationId: varchar("operation_id")
      .notNull()
      .references(() => schoolOperations.id, { onDelete: "restrict" }),
    eventType: text("event_type").notNull(),
    aggregateType: text("aggregate_type").notNull(),
    aggregateId: varchar("aggregate_id").notNull(),
    payload: jsonb("payload").notNull(),
    status: schoolOutboxStatusEnum("status").notNull().default("pending"),
    attempts: integer("attempts").notNull().default(0),
    lastError: text("last_error"),
    availableAt: timestamp("available_at", { withTimezone: true }).notNull().defaultNow(),
    createdAt: createdAt(),
    publishedAt: timestamp("published_at", { withTimezone: true }),
  },
  (t) => [
    uniqueIndex("school_outbox_operation_event_idx").on(t.operationId, t.eventType),
    index("school_outbox_pending_idx").on(t.organisationId, t.status, t.createdAt),
  ],
);

/**
 * Extra addresses a student's lesson notes also go to.
 *
 * The three columns on `students` are the school's record of a family —
 * maintained by the school, meaning what they have always meant. These are
 * different: additions made by whoever asked for them, which is why they are
 * rows rather than a fourth column. A student with their own address, a second
 * parent in a second household, a grandparent doing the lifts.
 *
 * `addedByUserId` is the point of the table. When notes turn up somewhere
 * nobody expected, "who added this address, and when" is the only question
 * worth asking, and a column on `students` cannot answer it. It is also what
 * lets somebody withdraw what they added without being able to remove what
 * somebody else did.
 */
export const studentNoteRecipients = pgTable(
  "student_note_recipients",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    /** Stored lower-case, like every other address here. */
    email: text("email").notNull(),
    /** Null only if that account has since been deleted. */
    addedByUserId: varchar("added_by_user_id").references(() => users.id, { onDelete: "set null" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("student_note_recipients_student_email_idx").on(t.studentId, t.email),
    index("student_note_recipients_student_idx").on(t.studentId),
  ],
);

/** Explicit parent↔child links, replacing the old "multi-profile" mechanism. */
export const parentStudents = pgTable(
  "parent_students",
  {
    id: id(),
    parentUserId: varchar("parent_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [uniqueIndex("parent_students_pair_idx").on(t.parentUserId, t.studentId)],
);

export const calendarFeedTokens = pgTable(
  "calendar_feed_tokens",
  {
    id: id(),
    organisationId: organisationId(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    /** A SHA-256 digest only; the bearer token is returned once at creation. */
    tokenHash: varchar("token_hash", { length: 64 }).notNull(),
    /** The exact self or guardian relationship that authorised this link. */
    authorityKind: text("authority_kind").notNull(),
    authorityId: varchar("authority_id").notNull(),
    createdAt: createdAt(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
  },
  (t) => [
    uniqueIndex("calendar_feed_tokens_hash_idx").on(t.tokenHash),
    uniqueIndex("calendar_feed_tokens_active_student_idx")
      .on(t.organisationId, t.userId, t.studentId)
      .where(sql`${t.revokedAt} is null`),
    check("calendar_feed_tokens_hash_check", sql`${t.tokenHash} ~ '^[0-9a-f]{64}$'`),
    check("calendar_feed_tokens_authority_kind_check", sql`${t.authorityKind} in ('student', 'parent')`),
    check("calendar_feed_tokens_authority_id_check", sql`${t.authorityId} <> ''`),
  ],
);

// ── Bookings, mirrored from the booking provider ─────────────────────────────

/**
 * A local mirror rather than a live fetch. Amelia offers no conditional
 * requests against a 5.4 MB payload at ~1.2 s per call, holds nothing before
 * 20 July 2026, and models participants as an array — all three of which this
 * pair of tables resolves. Kept fresh by a sync job plus webhooks; reads never
 * touch the provider.
 */
export const appointments = pgTable(
  "appointments",
  {
    id: id(),
    organisationId: organisationId(),
    /** The provider's own identifier, e.g. an Amelia appointment id. */
    externalId: text("external_id").notNull(),

    tutorId: varchar("tutor_id").references(() => tutorProfiles.id, { onDelete: "set null" }),
    externalProviderId: integer("external_provider_id"),

    serviceId: integer("service_id"),
    serviceName: text("service_name"),
    categoryId: integer("category_id"),
    categoryName: text("category_name"),

    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    status: text("status").notNull(),

    /** Derived from service/category, never from which tutor is teaching. */
    isSchools: boolean("is_schools").notNull().default(false),

    /** Provider payload as received. Contains personal data — never logged. */
    raw: jsonb("raw"),
    syncedAt: timestamp("synced_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("appointments_org_external_idx").on(t.organisationId, t.externalId),
    index("appointments_org_starts_idx").on(t.organisationId, t.startsAt),
    index("appointments_tutor_starts_idx").on(t.tutorId, t.startsAt),
  ],
);

// ── Lesson waitlist ────────────────────────────────────────────────────────────────────────

export const lessonWaitlistRequestKindEnum = pgEnum("lesson_waitlist_request_kind", ["pattern", "specific_time"]);
export const lessonWaitlistRequestPurposeEnum = pgEnum("lesson_waitlist_request_purpose", ["book", "move"]);
export const lessonWaitlistFallbackEnum = pgEnum("lesson_waitlist_fallback", ["keep", "cancel"]);
export const lessonWaitlistFallbackStateEnum = pgEnum("lesson_waitlist_fallback_state", ["scheduled", "running", "done", "not_needed", "failed"]);
export const lessonWaitlistDiscoveryEnum = pgEnum("lesson_waitlist_discovery", ["cancellation", "availability_check", "reschedule"]);

export const lessonWaitlistRequestStatusEnum = pgEnum("lesson_waitlist_request_status", [
  "pending",
  "approved",
  "rejected",
  "fulfilled",
  "withdrawn",
  "lapsed",
  "superseded",
]);

export const lessonWaitlistMatchStatusEnum = pgEnum("lesson_waitlist_match_status", [
  "open",
  "fulfilled",
  "exhausted",
]);

export const lessonWaitlistOfferStatusEnum = pgEnum("lesson_waitlist_offer_status", [
  "pending",
  "accepted",
  "rejected",
  "expired",
  "failed",
]);

export const lessonWaitlistShortNoticeApprovalStatusEnum = pgEnum(
  "lesson_waitlist_short_notice_approval_status",
  ["pending", "approved", "rejected", "revoked", "expired"],
);

/** One family's standing request for a tutor/service and recurring weekly windows. */
export const lessonWaitlistRequests = pgTable(
  "lesson_waitlist_requests",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),
    serviceId: integer("service_id").notNull(),
    serviceName: text("service_name").notNull(),
    status: lessonWaitlistRequestStatusEnum("status").notNull().default("pending"),
    kind: lessonWaitlistRequestKindEnum("kind").notNull().default("pattern"),
    purpose: lessonWaitlistRequestPurposeEnum("purpose"),
    requestedStartsAt: timestamp("requested_starts_at", { withTimezone: true }),
    requestedEndsAt: timestamp("requested_ends_at", { withTimezone: true }),
    sourceBookingExternalId: text("source_booking_external_id"),
    sourceAppointmentId: varchar("source_appointment_id"),
    sourceStartsAt: timestamp("source_starts_at", { withTimezone: true }),
    fallback: lessonWaitlistFallbackEnum("fallback"),
    actBy: timestamp("act_by", { withTimezone: true }),
    requestedByUserId: varchar("requested_by_user_id").references(() => users.id, { onDelete: "set null" }),
    requestedByActorKind: text("requested_by_actor_kind"),
    fallbackIdempotencyKey: uuid("fallback_idempotency_key"),
    fallbackState: lessonWaitlistFallbackStateEnum("fallback_state"),
    recheckRequestedAt: timestamp("recheck_requested_at", { withTimezone: true }),
    lastCheckedAt: timestamp("last_checked_at", { withTimezone: true }),
    availableCheckedAt: timestamp("available_checked_at", { withTimezone: true }),
    closedAt: timestamp("closed_at", { withTimezone: true }),
    shortNoticeApprovedAt: timestamp("short_notice_approved_at", { withTimezone: true }),
    shortNoticeOptIn: boolean("short_notice_opt_in").notNull().default(false),
    pausedAt: timestamp("paused_at", { withTimezone: true }),
    approvalNotifiedAt: timestamp("approval_notified_at", { withTimezone: true }),
    acceptingOfferId: varchar("accepting_offer_id"),
    acceptingUserId: varchar("accepting_user_id").references(() => users.id, { onDelete: "set null" }),
    acceptanceStartedAt: timestamp("acceptance_started_at", { withTimezone: true }),
    createdAt: createdAt(),
    decidedAt: timestamp("decided_at", { withTimezone: true }),
    decidedByStaffId: varchar("decided_by_staff_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /**
     * Why a request was turned down, in the words of whoever turned it down.
     *
     * Optional and short. A rejected request stops appearing in the family's
     * open list, and a request that simply vanishes with no explanation is the
     * thing this exists to prevent — the durable Inbox message that now
     * accompanies the decision carries this when there is one.
     */
    decisionReason: text("decision_reason"),
  },
  (t) => [
    index("lesson_waitlist_requests_queue_idx").on(
      t.organisationId,
      t.tutorId,
      t.serviceId,
      t.status,
      t.decidedAt,
    ),
    index("lesson_waitlist_requests_student_idx").on(t.organisationId, t.studentId, t.createdAt),
    foreignKey({ name: "lesson_waitlist_time_source_fk", columns: [t.sourceAppointmentId], foreignColumns: [appointments.id] }).onDelete("set null"),
    uniqueIndex("lesson_waitlist_one_open_move_idx").on(t.organisationId, t.sourceBookingExternalId).where(sql`${t.kind} = 'specific_time' and ${t.purpose} = 'move' and ${t.status} in ('pending', 'approved')`),
    index("lesson_waitlist_time_matching_idx").on(t.organisationId, t.kind, t.status, t.tutorId, t.requestedStartsAt),
    index("lesson_waitlist_time_deadline_idx").on(t.organisationId, t.kind, t.status, t.actBy),
    check("lesson_waitlist_specific_shape", sql`${t.kind} <> 'specific_time' or (${t.requestedStartsAt} is not null and ${t.requestedEndsAt} is not null and ${t.requestedEndsAt} > ${t.requestedStartsAt} and ${t.purpose} is not null and ${t.actBy} is not null)`),
    check("lesson_waitlist_move_shape", sql`${t.purpose} is distinct from 'move' or (${t.sourceBookingExternalId} is not null and ${t.sourceStartsAt} is not null and ${t.fallback} is not null and ${t.requestedByActorKind} is not null and ${t.requestedByActorKind} in ('student', 'parent'))`),
    check("lesson_waitlist_cancel_shape", sql`${t.fallback} is distinct from 'cancel' or ${t.fallbackIdempotencyKey} is not null`),
  ],
);

/** A recurring weekly window; Monday is 0 and times are school-local minutes. */
export const lessonWaitlistRequestPatterns = pgTable(
  "lesson_waitlist_request_patterns",
  {
    id: id(),
    requestId: varchar("request_id")
      .notNull()
      .references(() => lessonWaitlistRequests.id, { onDelete: "cascade" }),
    weekday: integer("weekday").notNull(),
    startsMinutes: integer("starts_minutes").notNull(),
    endsMinutes: integer("ends_minutes").notNull(),
  },
  (t) => [
    index("lesson_waitlist_patterns_request_idx").on(t.requestId),
    uniqueIndex("lesson_waitlist_patterns_window_idx").on(
      t.requestId,
      t.weekday,
      t.startsMinutes,
      t.endsMinutes,
    ),
    check("lesson_waitlist_patterns_weekday", sql`${t.weekday} between 0 and 6`),
    check("lesson_waitlist_patterns_order", sql`${t.endsMinutes} > ${t.startsMinutes}`),
    check(
      "lesson_waitlist_patterns_bounds",
      sql`${t.startsMinutes} >= 0 and ${t.endsMinutes} <= 1440`,
    ),
  ],
);

/** A slot observed becoming free; its facts stay frozen even if the mirror later changes. */
export const lessonWaitlistMatches = pgTable(
  "lesson_waitlist_matches",
  {
    id: id(),
    organisationId: organisationId(),
    // Nullable only after a hard provider deletion. The frozen fields below
    // preserve the match while allowing the mirror to remain a real mirror.
    sourceAppointmentId: varchar("source_appointment_id").references(() => appointments.id, {
      onDelete: "set null",
    }),
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),
    serviceId: integer("service_id").notNull(),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    discoveredAt: timestamp("discovered_at", { withTimezone: true }).notNull().defaultNow(),
    availabilityCheckAttempts: integer("availability_check_attempts").notNull().default(0),
    availabilityNextCheckAt: timestamp("availability_next_check_at", { withTimezone: true }).notNull().defaultNow(),
    status: lessonWaitlistMatchStatusEnum("status").notNull().default("open"),
    discovery: lessonWaitlistDiscoveryEnum("discovery").notNull().default("cancellation"),
    requestId: varchar("request_id"),
  },
  (t) => [
    uniqueIndex("lesson_waitlist_matches_source_slot_idx").on(
      t.organisationId,
      t.sourceAppointmentId,
      t.startsAt,
    ),
    foreignKey({ name: "lesson_waitlist_time_match_request_fk", columns: [t.requestId], foreignColumns: [lessonWaitlistRequests.id] }).onDelete("cascade"),
    index("lesson_waitlist_matches_open_idx").on(t.organisationId, t.status, t.discoveredAt),
    index("lesson_waitlist_matches_due_availability_idx").on(
      t.organisationId,
      t.availabilityNextCheckAt,
      t.startsAt,
      t.id,
    ).where(sql`${t.status} = 'open'`),
    check(
      "lesson_waitlist_matches_availability_attempts_nonnegative",
      sql`${t.availabilityCheckAttempts} >= 0`,
    ),
    uniqueIndex("lesson_waitlist_matches_request_slot_idx").on(t.organisationId, t.requestId, t.startsAt).where(sql`${t.status} = 'open'`),
    check("lesson_waitlist_matches_time_order", sql`${t.endsAt} > ${t.startsAt}`),
  ],
);

/** One turn in a match's FIFO queue, including the durable in-app unread marker. */
export const lessonWaitlistOffers = pgTable(
  "lesson_waitlist_offers",
  {
    id: id(),
    matchId: varchar("match_id")
      .notNull()
      .references(() => lessonWaitlistMatches.id, { onDelete: "cascade" }),
    requestId: varchar("request_id")
      .notNull()
      .references(() => lessonWaitlistRequests.id, { onDelete: "cascade" }),
    offeredAt: timestamp("offered_at", { withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    status: lessonWaitlistOfferStatusEnum("status").notNull().default("pending"),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    notificationReadAt: timestamp("notification_read_at", { withTimezone: true }),
  },
  (t) => [
    uniqueIndex("lesson_waitlist_offers_match_request_idx").on(t.matchId, t.requestId),
    uniqueIndex("lesson_waitlist_offers_one_pending_idx")
      .on(t.matchId)
      .where(sql`${t.status} = 'pending'`),
    index("lesson_waitlist_offers_expiry_idx").on(t.status, t.expiresAt),
  ],
);

/** Assigned-tutor decision for one exact learner and frozen slot. */
export const lessonWaitlistShortNoticeApprovals = pgTable(
  "lesson_waitlist_short_notice_approvals",
  {
    id: id(),
    organisationId: organisationId(),
    matchId: varchar("match_id")
      .notNull()
      .references(() => lessonWaitlistMatches.id, { onDelete: "cascade" }),
    requestId: varchar("request_id")
      .notNull()
      .references(() => lessonWaitlistRequests.id, { onDelete: "cascade" }),
    // Frozen identity, intentionally without a student FK: merge/deletion must
    // never transfer an approval to a different learner; requestId owns cleanup.
    studentId: varchar("student_id").notNull(),
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),
    serviceId: integer("service_id").notNull(),
    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    status: lessonWaitlistShortNoticeApprovalStatusEnum("status")
      .notNull()
      .default("pending"),
    requestedAt: timestamp("requested_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    decidedAt: timestamp("decided_at", { withTimezone: true }),
    decidedByStaffId: varchar("decided_by_staff_id").references(() => users.id, {
      onDelete: "set null",
    }),
    offerId: varchar("offer_id").references(() => lessonWaitlistOffers.id, {
      onDelete: "set null",
    }),
  },
  (t) => [
    uniqueIndex("lesson_waitlist_short_notice_approval_match_request_idx").on(
      t.matchId,
      t.requestId,
    ),
    uniqueIndex("lesson_waitlist_short_notice_approval_pending_match_idx")
      .on(t.matchId)
      .where(sql`${t.status} = 'pending'`),
    index("lesson_waitlist_short_notice_approval_queue_idx").on(
      t.organisationId,
      t.tutorId,
      t.status,
      t.requestedAt,
    ),
    check(
      "lesson_waitlist_short_notice_approval_time_order",
      sql`${t.endsAt} > ${t.startsAt}`,
    ),
  ],
);

/**
 * An observed cancellation deliberately declined by the waitlist.
 *
 * This is an operational fact, not a domain-authority audit: the provider
 * sync has no authenticated caller or person to attribute. The provider's
 * external appointment id is frozen here because that is what an incident
 * investigation starts with, even if the mirrored row later disappears.
 */
export const lessonWaitlistCancellationSkips = pgTable(
  "lesson_waitlist_cancellation_skips",
  {
    id: id(),
    organisationId: organisationId(),
    sourceAppointmentExternalId: text("source_appointment_external_id").notNull(),
    decisionPoint: text("decision_point").notNull(),
    reason: text("reason").notNull(),
    discoveredAt: timestamp("discovered_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("lesson_waitlist_cancellation_skips_appointment_idx").on(
      t.organisationId,
      t.sourceAppointmentExternalId,
      t.discoveredAt,
    ),
    index("lesson_waitlist_cancellation_skips_recent_idx").on(
      t.organisationId,
      t.discoveredAt,
    ),
  ],
);

/**
 * One row per participant. An appointment holding several bookings is a group
 * session or a waiting-list entry; both are represented faithfully here rather
 * than collapsed to the first participant.
 */
/**
 * Latest B01 learner-week decision for a cancellation candidate. The match and
 * request identifiers are retained as snapshots so the evidence survives
 * later domain-row cleanup; one row per match/request keeps dry-run storage
 * bounded while checked_at always reflects the most recent evaluation.
 */
export const waitlistSameWeekEligibilityAudits = pgTable(
  "waitlist_same_week_eligibility_audits",
  {
    id: id(),
    organisationId: organisationId(),
    matchId: text("match_id").notNull(),
    requestId: text("request_id").notNull(),
    decision: text("decision")
      .$type<"eligible" | "ineligible" | "unconfirmed">()
      .notNull(),
    reason: text("reason"),
    checkedAt: timestamp("checked_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      "waitlist_same_week_eligibility_audits_decision_check",
      sql`${t.decision} in ('eligible', 'ineligible', 'unconfirmed')`,
    ),
    uniqueIndex("waitlist_same_week_eligibility_audits_identity_idx").on(
      t.organisationId,
      t.matchId,
      t.requestId,
    ),
    index("waitlist_same_week_eligibility_audits_recent_idx").on(
      t.organisationId,
      t.checkedAt,
    ),
  ],
);

export const bookingAttributionRules = pgTable("booking_attribution_rules", {
  id: id(),
  organisationId: organisationId(),
  bookerKey: text("booker_key").notNull(),
  declaredName: text("declared_name").notNull(),
  studentId: varchar("student_id").notNull().references(() => students.id, { onDelete: "cascade" }),
  source: text("source").$type<"manual" | "family_claim" | "auto-created">().notNull(),
  confirmedByUserId: varchar("confirmed_by_user_id").references(() => users.id, { onDelete: "set null" }),
  confirmedAt: timestamp("confirmed_at", { withTimezone: true }).notNull().defaultNow(),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  revokedByUserId: varchar("revoked_by_user_id").references(() => users.id, { onDelete: "set null" }),
}, t => [
  uniqueIndex("booking_attribution_rules_active_pair_idx").on(t.organisationId, t.bookerKey, t.declaredName).where(sql`${t.revokedAt} is null`),
  index("booking_attribution_rules_student_idx").on(t.organisationId, t.studentId),
  check("booking_attribution_rules_name_check", sql`${t.declaredName} <> '' and ${t.declaredName} = lower(btrim(${t.declaredName}))`),
  check("booking_attribution_rules_booker_check", sql`${t.bookerKey} ~ '^(customer:[1-9][0-9]*|email:.+)$'`),
  check("booking_attribution_rules_source_check", sql`${t.source} in ('manual', 'family_claim', 'auto-created')`),
]);

export const appointmentBookings = pgTable(
  "appointment_bookings",
  {
    id: id(),
    appointmentId: varchar("appointment_id")
      .notNull()
      .references(() => appointments.id, { onDelete: "cascade" }),
    externalBookingId: text("external_booking_id").notNull(),

    externalCustomerId: integer("external_customer_id"),
    /**
     * Who booked, as the provider records them. Duplicated here rather than
     * dug out of `raw` because the admin queue has to show a human "who is
     * this?" before anyone can answer it.
     */
    customerName: text("customer_name"),
    customerEmail: text("customer_email"),
    /** Resolved during sync where possible; null when attribution is ambiguous. */
    studentId: varchar("student_id").references(() => students.id, { onDelete: "set null" }),
    /**
     * How `student_id` was arrived at: "custom-field", "customer-id", "email",
     * or "manual". A sync recomputes its own attributions but must never
     * overwrite "manual" — that is a person's decision, and it is the only
     * thing distinguishing "nobody has worked this out yet" from "somebody
     * already did".
     */
    attributionSource: text("attribution_source"),
    attributionRuleId: varchar("attribution_rule_id"),
    /** Keep an existing lesson visible until its newly readable name is confirmed. */
    attributionReviewRequired: boolean("attribution_review_required").notNull().default(false),

    cancellationOutcome: text("cancellation_outcome"),
    cancellationActor: text("cancellation_actor"),
    cancellationFunding: text("cancellation_funding"),
    cancellationChoice: text("cancellation_choice"),
    cancellationLate: boolean("cancellation_late"),
    cancellationPaymentReceived: boolean("cancellation_payment_received"),

    cancellationWaived: boolean("cancellation_waived"),
    cancellationCheckedAt: timestamp("cancellation_checked_at", { withTimezone: true }),
    /**
     * When a complete provider payload first omitted this participant. A later
     * payload that still omits them, after the sync's confirmation delay,
     * closes the booking; a payload that lists them again clears this.
     */
    providerAbsentAt: timestamp("provider_absent_at", { withTimezone: true }),
    status: text("status").notNull(),
    price: numeric("price", { precision: 10, scale: 2 }),
    couponId: integer("coupon_id"),

    /** Parsed from the provider's JSON-string fields. */
    customFields: jsonb("custom_fields"),
    info: jsonb("info"),
  },
  (t) => [
    uniqueIndex("appointment_bookings_external_idx").on(t.appointmentId, t.externalBookingId),
    index("appointment_bookings_student_idx").on(t.studentId),
    index("appointment_bookings_customer_idx").on(t.externalCustomerId),
    foreignKey({ name: "appointment_bookings_attribution_rule_id_fk", columns: [t.attributionRuleId],
      foreignColumns: [bookingAttributionRules.id] }).onDelete("set null"),
  ],
);

/** Local overrides layered on provider state, e.g. "cancelled with notice". */
export const appointmentStatusOverrides = pgTable(
  "appointment_status_overrides",
  {
    id: id(),
    appointmentId: varchar("appointment_id")
      .notNull()
      .references(() => appointments.id, { onDelete: "cascade" })
      .unique(),
    status: text("status").notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
);

// ── Lessons ──────────────────────────────────────────────────────────────────

export const lessons = pgTable(
  "lessons",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    tutorId: varchar("tutor_id").references(() => tutorProfiles.id, { onDelete: "set null" }),
    /** The authenticated user who actually wrote the current note. */
    editedByUserId: varchar("edited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /**
     * Populated by every creation path — admin, n8n and sync alike — so
     * attendance write-back actually has something to resolve against.
     */
    appointmentId: varchar("appointment_id").references(() => appointments.id, {
      onDelete: "set null",
    }),

    title: text("title").notNull(),
    content: text("content"),
    lessonDate: timestamp("lesson_date", { withTimezone: true }),
    attendanceStatus: text("attendance_status"),
    /**
     * Null while the note is a draft. A tutor writing up mid-lesson should not
     * be publishing half a sentence to a parent, so nothing reaches the family
     * until this is set.
     *
     * A timestamp rather than a flag, because "when did they first see this"
     * turns out to be the question people ask.
     */
    publishedAt: timestamp("published_at", { withTimezone: true }),
    /** Retained through withdrawal: only the first publication rings a bell. */
    firstPublishedAt: timestamp("first_published_at", { withTimezone: true }),
    notesSent: boolean("notes_sent").notNull().default(false),

    notionPageId: text("notion_page_id"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("lessons_student_date_idx").on(t.studentId, t.lessonDate),
    index("lessons_org_date_idx").on(t.organisationId, t.lessonDate),
    index("lessons_appointment_idx").on(t.appointmentId),
  ],
);

export const privateLessonStaffNotes = pgTable(
  "private_lesson_staff_notes",
  {
    id: id(),
    organisationId: organisationId(),
    lessonId: varchar("lesson_id")
      .notNull()
      .references(() => lessons.id, { onDelete: "cascade" }),
    content: text("content").notNull(),
    editedByUserId: varchar("edited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("private_lesson_staff_notes_org_lesson_idx").on(t.organisationId, t.lessonId),
  ],
);

export const lessonAttachments = pgTable(
  "lesson_attachments",
  {
    id: id(),
    lessonId: varchar("lesson_id")
      .notNull()
      .references(() => lessons.id, { onDelete: "cascade" }),
    fileName: text("file_name").notNull(),
    fileType: text("file_type"),
    fileSize: integer("file_size"),
    /** Object storage key. Served only through short-lived signed URLs. */
    objectKey: text("object_key").notNull(),
    resourceType: text("resource_type"),
    /** Quick-added links have no object bytes; file copies retain their own key. */
    externalUrl: text("external_url"),
    sourceKind: text("source_kind"),
    sourceId: text("source_id"),
    /** Tutors may reuse teaching assets; private assets retain their pupil's access. */
    shareable: boolean("shareable").notNull().default(true),
    uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    uploadedAt: createdAt(),
  },
  (t) => [
    index("lesson_attachments_lesson_idx").on(t.lessonId),
    uniqueIndex("lesson_attachments_source_idx").on(t.lessonId, t.sourceKind, t.sourceId),
  ],
);

export const lessonViews = pgTable(
  "lesson_views",
  {
    id: id(),
    lessonId: varchar("lesson_id")
      .notNull()
      .references(() => lessons.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    viewerType: text("viewer_type").notNull().default("student"),
    viewedAt: createdAt(),
  },
  (t) => [index("lesson_views_lesson_idx").on(t.lessonId)],
);

export const resources = pgTable(
  "resources",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id").references(() => students.id, { onDelete: "cascade" }),
    lessonId: varchar("lesson_id").references(() => lessons.id, { onDelete: "set null" }),
    name: text("name").notNull(),
    resourceType: text("resource_type"),
    description: text("description"),
    objectKey: text("object_key"),
    fileName: text("file_name"),
    notionPageId: text("notion_page_id"),
    hubProvider: text("hub_provider"),
    hubAssetId: text("hub_asset_id"),
    createdAt: createdAt(),
  },
  (t) => [index("resources_student_idx").on(t.studentId)],
);

// ── Practice ─────────────────────────────────────────────────────────────────

export const practiceAreas = pgTable(
  "practice_areas",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdBy: text("created_by").notNull().default("admin"),
    createdAt: createdAt(),
  },
  (t) => [index("practice_areas_student_idx").on(t.studentId)],
);

export const practiceSubcategories = pgTable(
  "practice_subcategories",
  {
    id: id(),
    areaId: varchar("area_id")
      .notNull()
      .references(() => practiceAreas.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdBy: text("created_by").notNull().default("admin"),
    createdAt: createdAt(),
  },
  (t) => [index("practice_subcategories_area_idx").on(t.areaId)],
);

export const practiceTodos = pgTable(
  "practice_todos",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    practiceAreaId: varchar("practice_area_id").references(() => practiceAreas.id, {
      onDelete: "set null",
    }),
    practiceSubcategoryId: varchar("practice_subcategory_id").references(
      () => practiceSubcategories.id,
      { onDelete: "set null" },
    ),
    title: text("title").notNull(),
    description: text("description"),
    completed: boolean("completed").notNull().default(false),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    completionStatus: text("completion_status"),
    readyForReviewAt: timestamp("ready_for_review_at", { withTimezone: true }),
    sourceLessonId: varchar("source_lesson_id").references(() => lessons.id, { onDelete: "set null" }),
    requestKey: varchar("request_key"),
    studentNotes: text("student_notes"),
    teacherNotes: text("teacher_notes"),
    /**
     * When the work stopped being set, for a task that had to be kept anyway.
     *
     * A child who has practised something, or whose tutor linked it to a goal,
     * keeps the record of it when the groove behind it is withdrawn or taken
     * off the lesson note. What they must not keep is an instruction to
     * practise something they can no longer open. Null for ordinary live work.
     */
    withdrawnAt: timestamp("withdrawn_at", { withTimezone: true }),
    createdBy: text("created_by").notNull().default("admin"),
    createdAt: createdAt(),
  },
  (t) => [
    index("practice_todos_student_idx").on(t.studentId),
    index("practice_todos_source_lesson_idx").on(t.sourceLessonId),
    uniqueIndex("practice_todos_org_request_key_idx").on(t.organisationId, t.requestKey).where(sql`${t.requestKey} is not null`),
  ],
);

export const practiceSessions = pgTable(
  "practice_sessions",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    /**
     * Whose account wrote the row: the student for themselves, or the parent
     * of a child too young to have a login. It is how a tutor reads "he says
     * twenty minutes" against "his mum says twenty minutes". Nullable, because
     * rows imported from the old system have nobody to name.
     */
    loggedByUserId: varchar("logged_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    startedAt: timestamp("started_at", { withTimezone: true }).notNull(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    /**
     * A single total in seconds. The old split of minutes *and* seconds needed
     * a startup job to keep the seconds column inside 0–59; one field cannot
     * drift from itself.
     */
    durationSeconds: integer("duration_seconds").notNull().default(0),
    instrument: text("instrument"),
    practiceTool: text("practice_tool"),
    name: text("name"),
    notes: text("notes"),
    needsWork: boolean("needs_work").notNull().default(false),
    /**
     * Filed by the timer rather than by a person, because nobody answered when
     * it asked. The length is the machine's best guess, so it is shown as one.
     */
    autoStopped: boolean("auto_stopped").notNull().default(false),
    practiceTodoId: varchar("practice_todo_id").references(() => practiceTodos.id, {
      onDelete: "set null",
    }),
    practiceAreaId: varchar("practice_area_id").references(() => practiceAreas.id, {
      onDelete: "set null",
    }),
    practiceSubcategoryId: varchar("practice_subcategory_id").references(
      () => practiceSubcategories.id,
      { onDelete: "set null" },
    ),
    bpmStart: integer("bpm_start"),
    bpmEnd: integer("bpm_end"),
  },
  (t) => [
    index("practice_sessions_student_started_idx").on(t.studentId, t.startedAt),
    index("practice_sessions_org_started_idx").on(t.organisationId, t.startedAt),
  ],
);

/**
 * A learner's own opt-in weekly practice target, in minutes. One row per
 * learner. It is only ever compared with saved practice for display: setting
 * or reaching it issues no award, ranking, notification or email.
 */
export const practiceWeeklyGoals = pgTable(
  "practice_weekly_goals",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    minutesPerWeek: integer("minutes_per_week").notNull(),
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check("practice_weekly_goals_minutes_check", sql`${t.minutesPerWeek} between 15 and 600`),
    uniqueIndex("practice_weekly_goals_student_idx").on(t.organisationId, t.studentId),
  ],
);

/**
 * A timer that is running right now.
 *
 * Kept apart from `practice_sessions` on purpose. A session in progress has no
 * length yet, and a row with a zero in `duration_seconds` would have to be
 * excluded by hand from the log, the seven-day total, the staff totals and
 * anything written later — one missed filter and somebody's page shows a
 * practice of no minutes. Nothing reads this table except the timer.
 *
 * At most one per student: a child and a parent must not each start one.
 */
export const practiceTimers = pgTable(
  "practice_timers",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    startedByUserId: varchar("started_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    startedAt: timestamp("started_at", { withTimezone: true }).notNull(),
    instrument: text("instrument"),
    /**
     * When somebody last said they were still practising — equal to
     * `started_at` until the first time the timer asks. Everything about when
     * to ask and when to give up is derived from this, so a browser left open
     * has no say in it.
     */
    confirmedAt: timestamp("confirmed_at", { withTimezone: true }).notNull(),
  },
  (t) => [uniqueIndex("practice_timers_student_idx").on(t.studentId)],
);

export const practiceSessionEdits = pgTable(
  "practice_session_edits",
  {
    id: id(),
    sessionId: varchar("session_id")
      .notNull()
      .references(() => practiceSessions.id, { onDelete: "cascade" }),
    editedByUserId: varchar("edited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    prevDurationSeconds: integer("prev_duration_seconds").notNull(),
    newDurationSeconds: integer("new_duration_seconds").notNull(),
    editedAt: createdAt(),
  },
  (t) => [index("practice_session_edits_session_idx").on(t.sessionId)],
);

export const practiceSessionAttachments = pgTable(
  "practice_session_attachments",
  {
    id: id(),
    practiceSessionId: varchar("practice_session_id")
      .notNull()
      .references(() => practiceSessions.id, { onDelete: "cascade" }),
    /**
     * A file in object storage, or a link to YouTube. One table rather than
     * two, because to everybody looking at a session they are the same thing:
     * something attached, in one list, added and removed the same way.
     */
    kind: attachmentKindEnum("kind").notNull().default("file"),
    /** The name as uploaded, or the video's address as pasted. */
    fileName: text("file_name").notNull(),
    fileType: text("file_type"),
    fileSize: integer("file_size"),
    /**
     * Where the bytes are. Null for a link, which has no bytes — which is why
     * this stopped being `not null`.
     */
    objectKey: text("object_key"),
    /** The YouTube video id, for a link. Null for a file. */
    videoId: text("video_id"),
    /** Private task association for assignment submissions. */
    practiceTodoId: varchar("practice_todo_id").references(() => practiceTodos.id, { onDelete: "set null" }),
    /** Optional learner reflection captured with this evidence. */
    reflection: text("reflection"),
    /** Reused by browser retries so one submission creates one row. */
    uploadRequestKey: varchar("upload_request_key"),
    uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    uploadedAt: createdAt(),
  },
  (t) => [
    index("practice_session_attachments_session_idx").on(t.practiceSessionId),
    index("practice_session_attachments_task_idx").on(t.practiceTodoId),
    uniqueIndex("practice_session_attachments_retry_idx")
      .on(t.practiceSessionId, t.uploadedByUserId, t.uploadRequestKey)
      .where(sql`${t.uploadRequestKey} is not null`),
    // Exactly one of the two, so a row cannot be both or neither.
    check(
      "practice_session_attachments_shape",
      sql`(${t.kind} = 'file' and ${t.objectKey} is not null and ${t.videoId} is null)
       or (${t.kind} = 'youtube' and ${t.videoId} is not null and ${t.objectKey} is null)`,
    ),
  ],
);

// ── Grooves ──────────────────────────────────────────────────────────────────

/**
 * A drum groove, as the Groove Builder writes one.
 *
 * **The query string is the groove.** `packages/groove`'s codec round-trips
 * a whole pattern — time signature, division, tempo, swing, every lane and
 * the stickings above them — through about two hundred characters:
 *
 * ```
 * ?TimeSig=4/4&Div=16&Tempo=90&H=|x-x-x-x-x-x-x-x-|&S=|----O-------O---|
 * ```
 *
 * So it is stored as that text and not decomposed into a column per lane.
 * The codec already defines what a groove is, on both sides of a vendored
 * copy; a second definition here could only ever disagree with it, and the
 * disagreement would show up as somebody's saved exercise coming back wrong.
 *
 * It also means a groove costs nothing to keep. There are no bytes in object
 * storage, nothing to expire, and a groove shared in March is still editable
 * in September — because what was kept is the music and not a picture of it.
 */
export const grooves = pgTable(
  "grooves",
  {
    id: id(),
    organisationId: organisationId(),
    title: text("title").notNull(),
    /** The canonical query string. See `@wmf/groove`'s `toUrl`/`parseUrl`. */
    query: text("query").notNull(),
    /**
     * Who wrote it. Null once that account is gone — the same `set null` as
     * `lessons.tutor_id`, and for the same reason: deleting the row would
     * quietly turn a tutor's library into nobody's, and the exercises are
     * still the exercises after they leave.
     */
    authorUserId: varchar("author_user_id").references(() => users.id, { onDelete: "set null" }),
    /**
     * What this was copied from, if anything.
     *
     * A student opening a groove their tutor set and changing it forks it
     * rather than editing the original: silently rewriting the exercise a
     * tutor set means the next lesson is about a groove neither of them is
     * looking at. This records where the copy came from.
     *
     * `set null` rather than cascade — losing the ancestor should not delete
     * the descendant's work.
     */
    originGrooveId: varchar("origin_groove_id").references((): AnyPgColumn => grooves.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    /**
     * Bumped on every write, and sent by the editor as the version it started
     * from. Lesson notes already taught us this: two people with the same
     * document open need the later save refused rather than merged, and a
     * tutor and a student working on one groove is exactly that case.
     */
    version: integer("version").notNull().default(1),
    /**
     * Marked, never removed.
     *
     * A groove is referenced by shares, lesson notes, School notes and renders
     * that have already been emailed. Deleting the row cascaded through every
     * one of them, which took School teaching history with it and contradicted
     * the promise the shares make by being withdrawn rather than deleted.
     * So deleting marks this instead, and every read excludes it.
     */
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
  },
  (t) => [
    index("grooves_org_author_idx").on(t.organisationId, t.authorUserId, t.updatedAt),
    check("grooves_version_positive", sql`${t.version} > 0`),
    check("grooves_title_present", sql`length(btrim(${t.title})) > 0`),
    // A groove that is not its own parent. Cheap to state, and the shape of
    // mistake that a copy-of-a-copy loop would otherwise make possible.
    check("grooves_origin_not_self", sql`${t.originGrooveId} is null or ${t.originGrooveId} <> ${t.id}`),
  ],
);

/**
 * A tutor giving a groove to a student.
 *
 * **A share is a decision, recorded as its own row, never a flag on the
 * groove.** Somebody said "have this" on a date, to a named person, for a
 * reason. That is a fact with an actor and a time, and the architecture rules
 * ask for durable evidence of exactly this shape — so it is a row, and the
 * `domain_audit_events` pair written alongside it says who decided and on what
 * authority.
 *
 * **Private students only, and there is no column here for a school pupil.**
 * School shares live in `school_groove_shares`, which mirrors this in School's
 * own vocabulary. One table discriminated by a `recipient_kind` would have
 * been less code and one forgotten `where` clause away from showing a tutor a
 * school pupil's name; the two domains are kept apart structurally everywhere
 * else they meet — `school_notes` beside `lessons` — and this is not the place
 * to make an exception. The `grooves` library itself is shared between them,
 * because a groove is an exercise and carries no personal data at all.
 */
export const grooveShares = pgTable(
  "groove_shares",
  {
    id: id(),
    organisationId: organisationId(),
    grooveId: varchar("groove_id")
      .notNull()
      // `restrict`, not `cascade`: deleting a groove is a mark on the row, and
      // anything that tried a real delete would be erasing history rather than
      // tidying up. Better a loud error than a quiet loss.
      .references(() => grooves.id, { onDelete: "restrict" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    /** Who shared it. Null once that account is gone; the share stands. */
    sharedByUserId: varchar("shared_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /** What the tutor said when they sent it. "Slowly, hands only." */
    note: text("note"),
    /**
     * The groove as it was when this was attached.
     *
     * A groove stays editable for as long as it exists, so pointing a
     * historical record at the live row means a groove attached in March shows
     * September's pattern. These two columns are the answer: what was actually
     * given, kept beside the reference rather than fetched through it.
     *
     * `grooveId` still points at the live groove, because "open the current
     * version of this" is a reasonable thing to offer. It is just no longer
     * the same question as "what was sent".
     */
    snapshotQuery: text("snapshot_query").notNull(),
    snapshotTitle: text("snapshot_title").notNull(),
    /**
     * The address this share is playable at, snapshot-backed.
     *
     * The same column, for the same reason, as `lesson_grooves.token`. A share
     * outlives the groove: deleting one is a mark on the row, so the share and
     * its practice to-do stay, and the family list went on linking to
     * `/grooves/<id>`, which by then answered 404. A child opening their
     * practice found nothing there.
     *
     * Addressing the share rather than the groove means the snapshot beside it
     * is what plays, so withdrawing the share takes the link with it and
     * deleting the groove does not.
     */
    token: text("token").notNull(),
    sharedAt: createdAt(),
    /**
     * Withdrawn, rather than deleted.
     *
     * The same reasoning as journalling a correction to a practice length: a
     * share that can vanish leaves a family looking at a groove nobody can
     * account for, and leaves nobody able to answer why a child stopped being
     * able to see their exercise.
     */
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    revokedByUserId: varchar("revoked_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (t) => [
    uniqueIndex("groove_shares_token_idx").on(t.token),
    index("groove_shares_student_idx").on(t.organisationId, t.studentId, t.revokedAt),
    index("groove_shares_groove_idx").on(t.grooveId),
    /**
     * One live share of a groove per student.
     *
     * Partial, on the live rows only, so re-sharing something withdrawn in
     * March is allowed and leaves both facts in place. Without it, a tutor
     * clicking twice gives a child the same exercise twice and there is no
     * single row to withdraw.
     */
    uniqueIndex("groove_shares_live_idx")
      .on(t.grooveId, t.studentId)
      .where(sql`${t.revokedAt} is null`),
    /**
     * No revoker without a revocation.
     *
     * Deliberately not the converse. `revoked_by_user_id` is `set null`, so a
     * share withdrawn by somebody who has since left keeps its `revoked_at`
     * and loses its name — which is the same shape as a lesson note whose
     * tutor has gone, and must stay legal.
     */
    check(
      "groove_shares_revoker_needs_revocation",
      sql`${t.revokedByUserId} is null or ${t.revokedAt} is not null`,
    ),
  ],
);

/**
 * A shared groove that is also work set before the next lesson.
 *
 * **`practice_todos` gains nothing.** Widening it with groove columns that are
 * null for every row the legacy import brought across would put a foreign
 * concern into the table practice is built on; a join says the same thing and
 * leaves both sides able to exist without the other. A groove can be shared
 * without being set as work — "have a look at this" — and a to-do can be
 * ordinary practice with no groove behind it, which is what every imported one
 * is.
 *
 * Private-domain only, and not by omission: `practice_todos` references
 * `students`, and practice as the portal models it is a private-side feature.
 * A school pupil is set work through School's own vocabulary.
 */
export const grooveAssignments = pgTable(
  "groove_assignments",
  {
    id: id(),
    organisationId: organisationId(),
    /**
     * The share this came from.
     *
     * Cascades: a share row is never deleted — it is revoked — so this only
     * fires when the groove itself goes, and a to-do pointing at a groove that
     * no longer exists is worse than no to-do.
     */
    grooveShareId: varchar("groove_share_id")
      .notNull()
      .references(() => grooveShares.id, { onDelete: "cascade" }),
    practiceTodoId: varchar("practice_todo_id")
      .notNull()
      .references(() => practiceTodos.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    // One to-do per share, and one share per to-do. Sharing the same groove
    // twice is already prevented upstream; this stops two rows describing the
    // same piece of work if anything ever gets past that.
    uniqueIndex("groove_assignments_share_idx").on(t.grooveShareId),
    uniqueIndex("groove_assignments_todo_idx").on(t.practiceTodoId),
  ],
);

/**
 * A groove on a lesson note.
 *
 * **Deliberately not `lesson_attachments`.** That table is about bytes in
 * object storage — `object_key` is `not null` — and a groove has no bytes.
 * Rendering one into a file to fit an existing table would be storing the
 * picture and throwing away the music, which is the one thing this whole
 * feature exists not to do.
 *
 * Private-domain only: `lessons` is the private side's record of what was
 * taught. School notes are `school_notes` and have their own step.
 */
export const lessonGrooves = pgTable(
  "lesson_grooves",
  {
    id: id(),
    organisationId: organisationId(),
    lessonId: varchar("lesson_id")
      .notNull()
      .references(() => lessons.id, { onDelete: "cascade" }),
    grooveId: varchar("groove_id")
      .notNull()
      // `restrict`, not `cascade`: deleting a groove is a mark on the row, and
      // anything that tried a real delete would be erasing history rather than
      // tidying up. Better a loud error than a quiet loss.
      .references(() => grooves.id, { onDelete: "restrict" }),
    /** The order a tutor put them in, which is the order they were taught. */
    position: integer("position").notNull().default(0),
    /**
     * The groove as it was when this was attached.
     *
     * A groove stays editable for as long as it exists, so pointing a
     * historical record at the live row means a groove attached in March shows
     * September's pattern. These two columns are the answer: what was actually
     * given, kept beside the reference rather than fetched through it.
     *
     * `grooveId` still points at the live groove, because "open the current
     * version of this" is a reasonable thing to offer. It is just no longer
     * the same question as "what was sent".
     */
    snapshotQuery: text("snapshot_query").notNull(),
    snapshotTitle: text("snapshot_title").notNull(),
    /**
     * The address this attachment is readable at, without signing in.
     *
     * A lesson note goes to a family by email, and a School note goes to a
     * pupil who has no portal account at all. Both used to link to
     * `/grooves/<id>`, which answers 404 unless the reader already has read
     * authority over the groove — attaching one to a note is not that, so the
     * "open and play it" link in a published note was frequently dead.
     *
     * The same shape as `groove_renders.token`: long, unguessable, and the
     * whole of the authority. It addresses the *attachment*, so it carries the
     * snapshot beside it and withdrawing the note takes the link with it.
     */
    token: text("token").notNull(),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("lesson_grooves_token_idx").on(t.token),
    index("lesson_grooves_lesson_idx").on(t.lessonId, t.position),
    // One groove per note. Attaching the same exercise twice is a slip, and a
    // note that lists it twice reads as two different things.
    uniqueIndex("lesson_grooves_unique_idx").on(t.lessonId, t.grooveId),
  ],
);

/**
 * A picture of a groove, for the places that cannot run the player.
 *
 * **A cache, not the record.** `grooves.query` is the groove; these are
 * regenerable from it by any browser, so losing every one of them loses
 * nothing and an R2 lifecycle rule on this prefix is a tidy-up rather than a
 * data loss. That is not true of anything else the portal puts there.
 *
 * **Keyed by a checksum of the query, not by the groove.** Two grooves with
 * the same pattern share one render, and editing a groove produces a new key
 * rather than overwriting the old one — so a note published in March keeps
 * showing what was actually sent, and cannot silently acquire September's
 * notation.
 *
 * **The checksum is computed on the server from the stored query**, never
 * supplied by the caller. Otherwise an upload could claim to be the rendering
 * of a pattern it is not.
 */
export const grooveRenders = pgTable(
  "groove_renders",
  {
    id: id(),
    organisationId: organisationId(),
    /** Which groove it was made from. For tidying up, not for lookup. */
    grooveId: varchar("groove_id")
      .notNull()
      // `restrict`, not `cascade`: deleting a groove is a mark on the row, and
      // anything that tried a real delete would be erasing history rather than
      // tidying up. Better a loud error than a quiet loss.
      .references(() => grooves.id, { onDelete: "restrict" }),
    /** SHA-256 of `grooves.query` at the moment it was rendered. */
    checksum: text("checksum").notNull(),
    objectKey: text("object_key").notNull(),
    /**
     * The unguessable name this is served under.
     *
     * An email is read hours later by a client with no session, so the image
     * needs an address that works without one. This is that address, and it is
     * long and random rather than derived from anything.
     *
     * Safe to expose because of what the thing is: a picture of a drum
     * pattern, carrying no name, no address and nothing about a child — the
     * same reason the groove library is shared across the private and School
     * domains. The one thing it does carry is the title a tutor typed, which
     * is why the title is theirs to choose and worth choosing plainly.
     */
    token: text("token").notNull(),
    createdAt: createdAt(),
  },
  (t) => [
    /**
     * One render per groove per pattern, not one per pattern.
     *
     * It was `(organisationId, checksum)`, so every groove with the same
     * pattern shared a row and a stored object — including a copy somebody
     * else had made. Whoever uploaded first owned the picture for all of them,
     * and since the bytes come from a browser, "first" was a thing an ordinary
     * student could arrange for a groove they had copied. Keyed by the groove
     * as well, a copy gets its own render and cannot speak for the original.
     *
     * The checksum stays in the key so that editing a groove makes a new row
     * rather than overwriting the old one: a note published in March goes on
     * showing what was actually sent.
     */
    uniqueIndex("groove_renders_groove_checksum_idx").on(
      t.organisationId,
      t.grooveId,
      t.checksum,
    ),
    uniqueIndex("groove_renders_token_idx").on(t.token),
  ],
);

/**
 * A groove given to a School pupil.
 *
 * **A separate table from `groove_shares`, deliberately.** One table
 * discriminated by a recipient kind would be less code and one forgotten
 * `where` clause away from showing a tutor a school pupil's name. The rules
 * say School data must not leak into private workflows, and everywhere else
 * the two domains meet the codebase keeps them apart structurally —
 * `school_notes` beside `lessons`, `school_pupils` beside `students`. This is
 * not the place to make an exception to save a read path.
 *
 * **What is shared is the `grooves` library itself**, and only that. A groove
 * is sixteen characters describing a backbeat: it carries no name, no address
 * and nothing about a person, which is what makes it the one thing safe to
 * hold in common between the two domains.
 *
 * **In School's vocabulary, not the private side's.** The actor is a subject
 * rather than a user id, as `school_notes.author_subject` is: a School actor
 * is not necessarily a portal account, and modelling one as a foreign key to
 * `users` would be asserting something the domain does not know.
 */
export const schoolGrooveShares = pgTable(
  "school_groove_shares",
  {
    id: id(),
    organisationId: organisationId(),
    grooveId: varchar("groove_id")
      .notNull()
      // `restrict`, not `cascade`: deleting a groove is a mark on the row, and
      // anything that tried a real delete would be erasing history rather than
      // tidying up. Better a loud error than a quiet loss.
      .references(() => grooves.id, { onDelete: "restrict" }),
    pupilId: varchar("pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    /** Who shared it, as School names an actor. */
    sharedBySubject: text("shared_by_subject").notNull(),
    note: text("note"),
    /**
     * The groove as it was when this was attached.
     *
     * A groove stays editable for as long as it exists, so pointing a
     * historical record at the live row means a groove attached in March shows
     * September's pattern. These two columns are the answer: what was actually
     * given, kept beside the reference rather than fetched through it.
     *
     * `grooveId` still points at the live groove, because "open the current
     * version of this" is a reasonable thing to offer. It is just no longer
     * the same question as "what was sent".
     */
    snapshotQuery: text("snapshot_query").notNull(),
    snapshotTitle: text("snapshot_title").notNull(),
    /**
     * The address this share is playable at, snapshot-backed.
     *
     * The same column, for the same reason, as `lesson_grooves.token`. A share
     * outlives the groove: deleting one is a mark on the row, so the share and
     * its practice to-do stay, and the family list went on linking to
     * `/grooves/<id>`, which by then answered 404. A child opening their
     * practice found nothing there.
     *
     * Addressing the share rather than the groove means the snapshot beside it
     * is what plays, so withdrawing the share takes the link with it and
     * deleting the groove does not.
     */
    token: text("token").notNull(),
    sharedAt: createdAt(),
    /** Withdrawn rather than deleted, as on the private side. */
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    revokedBySubject: text("revoked_by_subject"),
    /** The `school_operations` row that made it, for replay and for evidence. */
    sourceId: text("source_id"),
  },
  (t) => [
    uniqueIndex("school_groove_shares_token_idx").on(t.token),
    index("school_groove_shares_pupil_idx").on(t.organisationId, t.pupilId, t.revokedAt),
    index("school_groove_shares_groove_idx").on(t.grooveId),
    uniqueIndex("school_groove_shares_live_idx")
      .on(t.grooveId, t.pupilId)
      .where(sql`${t.revokedAt} is null`),
    check(
      "school_groove_shares_revoker_needs_revocation",
      sql`${t.revokedBySubject} is null or ${t.revokedAt} is not null`,
    ),
  ],
);

/**
 * A groove on a School note.
 *
 * The School counterpart of `lesson_grooves`, over `school_notes` rather than
 * `lessons`. Two tables for the same reason the shares are two tables: a join
 * from one domain's note to the other's would be the leak the rules forbid,
 * and a discriminator column is one forgotten `where` away from being one.
 */
export const schoolNoteGrooves = pgTable(
  "school_note_grooves",
  {
    id: id(),
    organisationId: organisationId(),
    noteId: varchar("note_id")
      .notNull()
      .references(() => schoolNotes.id, { onDelete: "cascade" }),
    grooveId: varchar("groove_id")
      .notNull()
      // `restrict`, not `cascade`: deleting a groove is a mark on the row, and
      // anything that tried a real delete would be erasing history rather than
      // tidying up. Better a loud error than a quiet loss.
      .references(() => grooves.id, { onDelete: "restrict" }),
    position: integer("position").notNull().default(0),
    /**
     * The groove as it was when this was attached.
     *
     * A groove stays editable for as long as it exists, so pointing a
     * historical record at the live row means a groove attached in March shows
     * September's pattern. These two columns are the answer: what was actually
     * given, kept beside the reference rather than fetched through it.
     *
     * `grooveId` still points at the live groove, because "open the current
     * version of this" is a reasonable thing to offer. It is just no longer
     * the same question as "what was sent".
     */
    snapshotQuery: text("snapshot_query").notNull(),
    snapshotTitle: text("snapshot_title").notNull(),
    /**
     * The address this attachment is readable at, without signing in.
     *
     * A lesson note goes to a family by email, and a School note goes to a
     * pupil who has no portal account at all. Both used to link to
     * `/grooves/<id>`, which answers 404 unless the reader already has read
     * authority over the groove — attaching one to a note is not that, so the
     * "open and play it" link in a published note was frequently dead.
     *
     * The same shape as `groove_renders.token`: long, unguessable, and the
     * whole of the authority. It addresses the *attachment*, so it carries the
     * snapshot beside it and withdrawing the note takes the link with it.
     */
    token: text("token").notNull(),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("school_note_grooves_token_idx").on(t.token),
    index("school_note_grooves_note_idx").on(t.noteId, t.position),
    uniqueIndex("school_note_grooves_unique_idx").on(t.noteId, t.grooveId),
  ],
);

/**
 * Work set for a School pupil before the next lesson.
 *
 * **Staff-facing, and that is a fact about the domain rather than a decision
 * taken here.** A School pupil has no account: nothing in `Principal` knows
 * what a pupil is, and there is no route by which one signs in. So where the
 * private side's `practice_todos` is a thing a child ticks off, this is a
 * thing a tutor records and marks — the same information, kept by the only
 * person in this domain who can keep it. If School ever gets logins, the
 * column that changes is who may write `completed`, and nothing else.
 *
 * Deliberately not `practice_todos` with a nullable pupil column. That table
 * references `students`, and giving it a second, mutually exclusive owner is
 * exactly the shape this codebase keeps out of the School boundary.
 */
export const schoolPracticeTodos = pgTable(
  "school_practice_todos",
  {
    id: id(),
    organisationId: organisationId(),
    pupilId: varchar("pupil_id")
      .notNull()
      .references(() => schoolPupils.id, { onDelete: "restrict" }),
    title: text("title").notNull(),
    description: text("description"),
    completed: boolean("completed").notNull().default(false),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    /** What the tutor observed. There is no pupil to write the other half. */
    teacherNotes: text("teacher_notes"),
    createdBySubject: text("created_by_subject").notNull(),
    completedBySubject: text("completed_by_subject"),
    /** The `school_operations` row that made it. */
    sourceId: text("source_id"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("school_practice_todos_pupil_idx").on(t.organisationId, t.pupilId, t.completed),
    check("school_practice_todos_title_present", sql`length(btrim(${t.title})) > 0`),
    check(
      "school_practice_todos_completion_complete",
      sql`${t.completed} = false or ${t.completedAt} is not null`,
    ),
  ],
);

/**
 * A School share that is also work set — the counterpart of
 * `groove_assignments`.
 */
export const schoolGrooveAssignments = pgTable(
  "school_groove_assignments",
  {
    id: id(),
    organisationId: organisationId(),
    schoolGrooveShareId: varchar("school_groove_share_id")
      .notNull()
      .references(() => schoolGrooveShares.id, { onDelete: "cascade" }),
    schoolPracticeTodoId: varchar("school_practice_todo_id")
      .notNull()
      .references(() => schoolPracticeTodos.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("school_groove_assignments_share_idx").on(t.schoolGrooveShareId),
    uniqueIndex("school_groove_assignments_todo_idx").on(t.schoolPracticeTodoId),
  ],
);

// ── Provider sync ────────────────────────────────────────────────────────────

/**
 * One row per attempt to mirror the booking provider.
 *
 * Recorded because a sync that stops running is invisible otherwise: the data
 * simply stops changing, and stale bookings look exactly like a quiet week.
 * Failures are rows too — a run that errored is a fact worth keeping, not an
 * absence.
 */
export const syncRuns = pgTable(
  "sync_runs",
  {
    id: id(),
    organisationId: organisationId(),

    /** "schedule", "webhook" or "manual" — why this run happened. */
    trigger: text("trigger").notNull(),

    startedAt: timestamp("started_at", { withTimezone: true }).notNull().defaultNow(),
    /** Null while in flight, which is also how an overlap guard sees it. */
    finishedAt: timestamp("finished_at", { withTimezone: true }),

    windowFrom: timestamp("window_from", { withTimezone: true }),
    windowTo: timestamp("window_to", { withTimezone: true }),

    appointmentsSeen: integer("appointments_seen").notNull().default(0),
    bookingsSeen: integer("bookings_seen").notNull().default(0),
    matched: integer("matched").notNull().default(0),
    unresolved: integer("unresolved").notNull().default(0),
    /** Cancellations correctly declined by waitlist matching during this pass. */
    skippedWaitlistCancellations: integer("skipped_waitlist_cancellations").notNull().default(0),

    /** The failure message, or null if the run finished. Never the payload. */
    error: text("error"),
  },
  (t) => [index("sync_runs_org_started_idx").on(t.organisationId, t.startedAt)],
);

// ── Auth plumbing ────────────────────────────────────────────────────────────

/** connect-pg-simple's store. Shape is dictated by that library. */
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire", { precision: 6, withTimezone: false }).notNull(),
  },
  (t) => [index("sessions_expire_idx").on(t.expire)],
);

/**
 * Outstanding password reset links.
 *
 * The row holds a SHA-256 of the token, never the token itself — see
 * `tokens.ts` for why. A lookup therefore hashes what arrived and searches by
 * that, which is also why the hash is unique rather than the token.
 *
 * `userId` rather than an address, because the two are not the same question.
 * A child signs in by username and has no address of their own, so the link
 * goes to `users.recovery_email` — a parent's. `sentTo` records where it
 * actually went, which is the thing to check when somebody says they never
 * received it.
 */
export const passwordResetTokens = pgTable(
  "password_reset_tokens",
  {
    id: id(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull().unique(),
    /** The address the link was sent to, for support rather than for lookup. */
    sentTo: text("sent_to").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    /** Set the moment it is spent, which is what makes it single-use. */
    usedAt: timestamp("used_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [index("password_reset_tokens_user_idx").on(t.userId)],
);

/**
 * Outstanding "confirm your address" links.
 *
 * Same shape and same rules as the reset tokens above — the hash rather than
 * the token, single use, an expiry — for the same reason: this link is what
 * decides whether an account gets attached to a child's records.
 *
 * `email` records which address was being claimed, because an account can in
 * principle be sent a confirmation for one address while holding another, and
 * the link must confirm the address it was sent to rather than whatever the
 * row happens to say by the time it is clicked.
 */
export const emailConfirmationTokens = pgTable(
  "email_confirmation_tokens",
  {
    id: id(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull().unique(),
    email: text("email").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    usedAt: timestamp("used_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [index("email_confirmation_tokens_user_idx").on(t.userId)],
);

/**
 * An outstanding invitation to teach here.
 *
 * A tutor account is not a thing anybody may ask for. The role reads children's
 * records — lesson notes, practice, a family's addresses — so it is granted by
 * an owner naming somebody, never claimed by whoever fills in a form. This
 * table is that naming, held until the person proves the address it was sent
 * to.
 *
 * Same rules as the two token tables above, for the same reason: the hash
 * rather than the token, single use, an expiry. This link grants staff access
 * to a school's records, which is a larger thing than either of them.
 *
 * `displayName` travels on the row because the owner types it when inviting.
 * It is the school's name for that tutor — the one already on the diary and on
 * every lesson note they will write — so it is set by the person who knows it
 * and merely confirmed by the tutor. It is also what makes the pending list
 * readable: an address on its own does not say who is being waited for.
 *
 * `revokedAt` is separate from `acceptedAt` rather than folded in with it,
 * because "she never clicked it" and "we changed our minds" are different
 * answers to the same question, and only one of them means anything went wrong.
 */
export const tutorInvitations = pgTable(
  "tutor_invitations",
  {
    id: id(),
    organisationId: organisationId(),
    /** Stored lower-case, like every other address here. */
    email: text("email").notNull(),
    displayName: text("display_name").notNull(),
    /**
     * The tutor this invitation is for, when it is somebody who already
     * teaches here rather than somebody new.
     *
     * Null for an ordinary invitation. Set when an owner is reaching an
     * existing profile — in practice one adopted from the booking provider,
     * whose account sits on an undeliverable `@invalid.local` address and
     * therefore cannot be reached any other way. Accepting moves that account
     * onto the invited address rather than creating a second one beside it,
     * which is what keeps the profile's `amelia_provider_id`, and the diary
     * hanging off it, attached to the person.
     *
     * Cascades: an invitation to a profile that no longer exists is not an
     * invitation to anything.
     */
    tutorProfileId: varchar("tutor_profile_id").references(() => tutorProfiles.id, {
      onDelete: "cascade",
    }),
    /** Owner-approved teaching identity setup, acted on only after acceptance. */
    bookingSetup: jsonb("booking_setup").$type<{ firstName: string; lastName: string; locationId: number | null }>(),
    /** Null only if that owner's account has since been deleted. */
    invitedByUserId: varchar("invited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    tokenHash: text("token_hash").notNull().unique(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    acceptedAt: timestamp("accepted_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [
    index("tutor_invitations_org_email_idx").on(t.organisationId, t.email),
    index("tutor_invitations_org_created_idx").on(t.organisationId, t.createdAt),
  ],
);

/**
 * A one-use invitation for a family member or adult learner to establish a
 * Hub account. The student reference explains why it was sent and supports
 * the staff view; acceptance links only through the usual confirmed-address
 * registration rule, never through this foreign key.
 */
export const hubInvitations = pgTable(
  "hub_invitations",
  {
    id: id(),
    organisationId: organisationId(),
    /** Canonical, lower-case address proved by whoever uses the token. */
    email: text("email").notNull(),
    studentId: varchar("student_id").references(() => students.id, { onDelete: "set null" }),
    invitedByUserId: varchar("invited_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    tokenHash: text("token_hash").notNull().unique(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    mailQueuedAt: timestamp("mail_queued_at", { withTimezone: true }),
    acceptedAt: timestamp("accepted_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    createdAt: createdAt(),
  },
  (t) => [index("hub_invitations_org_email_idx").on(t.organisationId, t.email)],
);

/**
 * A verified Amelia payer may establish a Hub account before staff can safely
 * assign the purchase to a student. This row proves only the payer inbox; it
 * never grants credits or a parent/student relationship on its own.
 */
export const hubPurchaseClaims = pgTable(
  "hub_purchase_claims",
  {
    id: id(),
    organisationId: organisationId(),
    ameliaPaymentId: integer("amelia_payment_id").notNull(),
    ameliaBookingId: integer("amelia_booking_id").notNull(),
    payerEmail: text("payer_email").notNull(),
    purchase: jsonb("purchase").$type<PurchaseClaimDetails>(),
    invitationId: varchar("invitation_id").references(() => hubInvitations.id, { onDelete: "set null" }),
    deliveryStartedAt: timestamp("delivery_started_at", { withTimezone: true }),
    verifiedUserId: varchar("verified_user_id").references(() => users.id, { onDelete: "set null" }),
    verifiedAt: timestamp("verified_at", { withTimezone: true }),
    selectedStudentId: varchar("selected_student_id").references(() => students.id),
    selectedAt: timestamp("selected_at", { withTimezone: true }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("hub_purchase_claims_payment_idx").on(t.organisationId, t.ameliaPaymentId),
    index("hub_purchase_claims_email_idx").on(t.organisationId, t.payerEmail),
  ],
);

/** Owner-confirmed, prospective learner defaults for one payer and lesson service. */
export const familyBookingRoutes = pgTable("family_booking_routes", {
  id: id(),
  organisationId: organisationId(),
  parentUserId: varchar("parent_user_id").notNull(),
  parentEmail: text("parent_email").notNull(),
  emailConfirmedAt: timestamp("email_confirmed_at", { withTimezone: true }).notNull(),
  payerCustomerId: integer("payer_customer_id").notNull(),
  serviceId: integer("service_id").notNull(),
  serviceName: text("service_name").notNull(),
  studentId: varchar("student_id").notNull(),
  parentNames: text("parent_names").array().notNull(),
  familyStudentIds: text("family_student_ids").array().notNull(),
  confirmedByUserId: varchar("confirmed_by_user_id").notNull(),
  confirmedAt: timestamp("confirmed_at", { withTimezone: true }).notNull().defaultNow(),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
}, (t) => [
  foreignKey({ name: "family_booking_routes_parent_user_id_fkey", columns: [t.parentUserId], foreignColumns: [users.id] }),
  foreignKey({ name: "family_booking_routes_student_id_fkey", columns: [t.studentId], foreignColumns: [students.id] }),
  foreignKey({ name: "family_booking_routes_confirmed_by_user_id_fkey", columns: [t.confirmedByUserId], foreignColumns: [users.id] }),
  uniqueIndex("family_booking_routes_active_idx").on(t.organisationId, t.payerCustomerId, t.serviceId).where(sql`${t.revokedAt} is null`),
  check("family_booking_routes_service_check", sql`${t.payerCustomerId} > 0 and ${t.serviceId} > 0`),
  check("family_booking_routes_email_check", sql`${t.parentEmail} <> '' and ${t.parentEmail} = lower(btrim(${t.parentEmail}))`),
  check("family_booking_routes_family_check", sql`cardinality(${t.familyStudentIds}) > 0 and ${t.studentId} = any(${t.familyStudentIds})`),
]);

/** Server-validated learner choice, bound to one exact Amelia participant. */
export const bookingLearnerBindings = pgTable("booking_learner_bindings", {
  id: id(),
  organisationId: organisationId(),
  externalBookingId: text("external_booking_id").notNull(),
  externalAppointmentId: text("external_appointment_id").notNull(),
  studentId: varchar("student_id").notNull().references(() => students.id),
  actorUserId: varchar("actor_user_id").references(() => users.id),
  source: text("source").notNull(),
  familyBookingRouteId: varchar("family_booking_route_id"),
  createdAt: createdAt(),
}, (t) => [
  foreignKey({ name: "booking_learner_bindings_family_booking_route_id_fkey", columns: [t.familyBookingRouteId], foreignColumns: [familyBookingRoutes.id] }),
  uniqueIndex("booking_learner_bindings_booking_idx").on(t.organisationId, t.externalBookingId),
  check("booking_learner_bindings_source_check", sql`${t.source} in ('family_claim', 'hub_booking', 'family_route')`),
  check("booking_learner_bindings_route_check", sql`(${t.source} = 'family_route') = (${t.familyBookingRouteId} is not null)`),
]);

/** Explicit payer consent for future purchases; changed authority suspends it permanently until renewed. */
export const familyPurchasePreferences = pgTable("family_purchase_preferences", {
  id: id(),
  organisationId: organisationId(),
  userId: varchar("user_id").notNull().references(() => users.id),
  payerCustomerId: integer("payer_customer_id").notNull(),
  lessonServiceId: integer("lesson_service_id"),
  lessonTypeName: text("lesson_type_name"),
  payerEmail: text("payer_email").notNull(),
  payerKind: text("payer_kind").$type<"family" | "self">().notNull(),
  studentId: varchar("student_id").notNull().references(() => students.id),
  sourceClaimId: varchar("source_claim_id").notNull(),
  emailConfirmedAt: timestamp("email_confirmed_at", {withTimezone: true}).notNull(),
  householdFingerprint: text("household_fingerprint").notNull(),
  automaticEligible: boolean("automatic_eligible").notNull().default(false),
  consentedAt: timestamp("consented_at", {withTimezone: true}).notNull(),
  suspendedAt: timestamp("suspended_at", {withTimezone: true}),
  revokedAt: timestamp("revoked_at", {withTimezone: true}),
  createdAt: createdAt(),
  updatedAt: timestamp("updated_at", {withTimezone: true}).notNull().defaultNow(),
}, (t) => [
  foreignKey({name: "family_purchase_preferences_source_claim_fk", columns: [t.sourceClaimId], foreignColumns: [hubPurchaseClaims.id]}),
  uniqueIndex("family_purchase_preferences_owner_payer_idx").on(t.organisationId, t.userId, t.payerCustomerId, t.lessonServiceId),
  uniqueIndex("family_purchase_preferences_legacy_payer_idx").on(t.organisationId, t.userId, t.payerCustomerId)
    .where(sql`${t.lessonServiceId} is null`),
  check("family_purchase_preferences_lesson_service_check", sql`${t.lessonServiceId} is null or ${t.lessonServiceId} > 0`),
  check("family_purchase_preferences_customer_check", sql`${t.payerCustomerId} > 0`),
  check("family_purchase_preferences_payer_kind_check", sql`${t.payerKind} in ('family', 'self')`),
  check("family_purchase_preferences_email_check", sql`${t.payerEmail} <> '' and ${t.payerEmail} = lower(btrim(${t.payerEmail}))`),
]);

// ── Email ────────────────────────────────────────────────────────────────────

/**
 * Where a message got to.
 *
 * The first four are what this end knows: queued, in flight, accepted by the
 * provider, given up on. The last three are what the provider tells us
 * afterwards, and they are the ones that answer the question people actually
 * ask — "sent" means a server took it, which is entirely compatible with a
 * hard bounce a second later.
 */
export const emailStatusEnum = pgEnum("email_status", [
  "queued",
  "sending",
  "sent",
  "failed",
  "delivered",
  "bounced",
  "complained",
  "suppressed",
]);

export type EmailStatus = (typeof emailStatusEnum.enumValues)[number];

/**
 * Everything the portal has ever tried to send, and what became of it.
 *
 * An outbox rather than a call to a provider inside the request that caused
 * it, for three reasons that all showed up in the system this replaces:
 *
 *  - A provider being slow or down became a 500 in front of whoever was
 *    registering. Here the row is written in the same transaction as the thing
 *    that caused it, and the sending happens afterwards.
 *  - A failure was a line in a log, if that. Here it is a row with an error on
 *    it, and "did Ellie's mum get the notes" is a query rather than a memory.
 *  - Retrying meant somebody noticing. Here `availableAt` carries the backoff
 *    and the drain picks it up on its own.
 *
 * `dedupeKey` is what stops a retry, a double click or a second scheduler from
 * sending the same thing twice: the caller names the message — `reset:<id>`,
 * `notes:<lessonId>` — and the unique index refuses the second one. Null for
 * anything genuinely repeatable.
 */
export const emailMessages = pgTable(
  "email_messages",
  {
    id: id(),
    organisationId: organisationId(),

    toEmail: text("to_email").notNull(),
    toName: text("to_name"),
    replyTo: text("reply_to"),
    subject: text("subject").notNull(),
    bodyText: text("body_text").notNull(),
    bodyHtml: text("body_html"),
    /**
     * Files travelling with the message, base64-encoded, or null for the
     * ordinary case of none.
     *
     * On the row rather than fetched at send time, so a queued message is
     * self-contained: the outbox's whole design is that the row is written in
     * the transaction that caused it and sent later, possibly by a different
     * process after a restart, and an attachment resolved at send time would
     * be one more thing that can have changed or vanished in between.
     *
     * Not a general file store. See `EmailAttachment` in the mailer for what
     * belongs here and what belongs behind a link.
     */
    attachments: jsonb("attachments"),
    /** Which template produced this, so a batch can be found later. */
    template: text("template").notNull(),

    status: emailStatusEnum("status").notNull().default("queued"),
    attempts: integer("attempts").notNull().default(0),
    /** When the drain may next pick this up. Moved forward by the backoff. */
    availableAt: timestamp("available_at", { withTimezone: true }).notNull().defaultNow(),
    lastError: text("last_error"),
    /** The provider's own id, which is what its dashboard searches by. */
    providerMessageId: text("provider_message_id"),
    sentAt: timestamp("sent_at", { withTimezone: true }),

    dedupeKey: text("dedupe_key"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // The drain's query: the queue, in the order it should go out.
    index("email_messages_pending_idx").on(t.status, t.availableAt),
    index("email_messages_org_created_idx").on(t.organisationId, t.createdAt),
    // Null keys do not collide in Postgres, so unrepeatable messages are
    // constrained and repeatable ones are left alone.
    uniqueIndex("email_messages_dedupe_idx").on(t.organisationId, t.dedupeKey),
  ],
);

// ── Tutor invoicing ──────────────────────────────────────────────────────────

/**
 * What the school pays a tutor, per lesson.
 *
 * **In its own table rather than as two columns on `tutor_profiles`**, for the
 * same reason `tutor_dbs` is: a staff query should not be able to drag
 * somebody's pay into a response by accident. Every screen that lists tutors
 * selects from the profile, and a rate column there is one forgotten `select`
 * away from being rendered to a colleague. Reaching it takes a deliberate
 * join, from routes an owner alone can call.
 *
 * Pence, as integers. See `packages/shared/src/invoicing.ts` for why there is
 * no floating point anywhere near this feature.
 *
 * Null means "nobody has set this", which is not zero — a tutor with no
 * 60-minute rate who taught an hour gets a flagged line rather than a free one.
 */
export const tutorInvoiceRates = pgTable(
  "tutor_invoice_rates",
  {
    id: id(),
    organisationId: organisationId(),
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "cascade" }),
    /** Per 30-minute lesson, in pence. */
    minutes30Pence: integer("minutes_30_pence"),
    /** Per 60-minute lesson, in pence. */
    minutes60Pence: integer("minutes_60_pence"),
    /** Who last changed it. Pay is a decision somebody made, not a fact. */
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("tutor_invoice_rates_tutor_idx").on(t.tutorId),
    check(
      "tutor_invoice_rates_non_negative",
      sql`(${t.minutes30Pence} is null or ${t.minutes30Pence} >= 0) and (${t.minutes60Pence} is null or ${t.minutes60Pence} >= 0)`,
    ),
  ],
);

/**
 * The organisation's invoicing settings. One row.
 *
 * `tasterCouponIds` is the whole basis of the Taster exclusion. The mirror
 * stores the provider's numeric coupon id and not its code, so which number
 * means "Taster" is something an owner tells us rather than something the data
 * knows. Kept as a list because a school that runs a Taster offer twice ends
 * up with two coupons and needs both excluded.
 *
 * Deliberately *not* "any coupon": the five-lesson blocks are coupons too, and
 * those are ordinary paid teaching.
 */
export const invoiceSettings = pgTable(
  "invoice_settings",
  {
    id: id(),
    organisationId: organisationId(),
    /** Provider coupon ids whose lessons are never charged to the school. */
    tasterCouponIds: integer("taster_coupon_ids").array().notNull().default(sql`'{}'::integer[]`),
    /**
     * Provider coupon ids that are gift vouchers. A voucher lesson is ordinary
     * paid teaching for the tutor; the list only lets the teaching income
     * report say "voucher" rather than "on a block" for it.
     */
    voucherCouponIds: integer("voucher_coupon_ids").array().notNull().default(sql`'{}'::integer[]`),
    /** Counts invoices for the reference on the document. */
    nextSequence: integer("next_sequence").notNull().default(1),
    /** Free text put at the foot of every invoice, e.g. payment terms. */
    footerNote: text("footer_note"),
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [uniqueIndex("invoice_settings_org_idx").on(t.organisationId)],
);

/**
 * Where an invoice is. See `canSendInvoice` / `canVoidInvoice` in
 * `@wmf/shared/invoicing` for what may be done in each state — the rules live
 * there so no route can invent its own.
 *
 * `queued` and `sent` are deliberately different facts. Pressing send puts a
 * message in the outbox; the provider accepting it happens later and can fail
 * permanently. Collapsing the two would leave a row saying "sent" for an
 * invoice the tutor never received, with no way to retry because it was no
 * longer a draft.
 */
export const tutorInvoiceStatusEnum = pgEnum("tutor_invoice_status", [
  "draft",
  "queued",
  "sent",
  "delivery_failed",
  "void",
]);

export type TutorInvoiceStatus = (typeof tutorInvoiceStatusEnum.enumValues)[number];

/**
 * One period's pay for one tutor.
 *
 * **Everything that decided the total is copied onto the row.** The rates are
 * snapshotted rather than joined, so changing what a tutor is paid next term
 * cannot silently restate what they were paid last term — an invoice already
 * sent must still say what it said when it was sent, and a document that
 * re-prices itself when a setting changes is one nobody can reconcile against
 * their bank statement.
 *
 * The period is two `date` columns rather than instants, because "1–30
 * September" is a claim about calendar days in the school's own timezone, and
 * it is what both people read. Converting to instants happens at query time.
 */
export const tutorInvoices = pgTable(
  "tutor_invoices",
  {
    id: id(),
    organisationId: organisationId(),
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),

    /** `WMF-2026-09-0003`. Unique per organisation, and on the document. */
    reference: text("reference").notNull(),
    /** Inclusive, in the organisation's timezone. */
    periodStart: date("period_start").notNull(),
    /** Inclusive. */
    periodEnd: date("period_end").notNull(),

    status: tutorInvoiceStatusEnum("status").notNull().default("draft"),

    /** The rates as they stood when this was built. Null where none was set. */
    rate30Pence: integer("rate_30_pence"),
    rate60Pence: integer("rate_60_pence"),
    /** Sum of the charged lines. Stored, so the document never re-totals. */
    totalPence: integer("total_pence").notNull().default(0),
    chargedCount: integer("charged_count").notNull().default(0),
    /**
     * Whether a person still has to decide something before this can go out.
     *
     * Persisted rather than re-derived at send time, because the whole point
     * of snapshotting an invoice is that the approved document does not
     * mutate underneath the approval. Send is refused while it is true: an
     * unresolved line contributes £0, so sending would underpay somebody by
     * exactly the value of the work nobody had priced.
     */
    needsAttention: boolean("needs_attention").notNull().default(false),
    /** How many lines that is about, so it can be shown and audited. */
    unresolvedCount: integer("unresolved_count").notNull().default(0),
    /**
     * Charged lessons the family had not paid for when this was built, and
     * whether anybody could be asked. `coverageChecked` false means the
     * booking system was unreachable and the count asserts nothing.
     */
    unpaidCount: integer("unpaid_count").notNull().default(0),
    coverageChecked: boolean("coverage_checked").notNull().default(false),
    /** False when a coverage answer was appointment-level rather than per child. */
    coverageParticipantPrecise: boolean("coverage_participant_precise")
      .notNull()
      .default(true),

    /** The rendered document, exactly as sent. */
    pdf: text("pdf_base64"),

    /** Which schedule produced it, or null for one an owner asked for. */
    scheduleId: varchar("schedule_id"),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    sentByUserId: varchar("sent_by_user_id").references(() => users.id, { onDelete: "set null" }),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    sentToEmail: text("sent_to_email"),
    /** The outbox row, so "did it actually go" is a join rather than a memory. */
    emailMessageId: varchar("email_message_id").references(() => emailMessages.id, {
      onDelete: "set null",
    }),

    /**
     * When the school recorded paying this, and who said so.
     *
     * **Orthogonal to `status`, on purpose.** The status describes the
     * document — built, queued, delivered, withdrawn — and whether the money
     * has left the bank is a fact about something else. Folding it in would
     * leave an invoice whose email bounced but which was paid anyway with
     * nowhere to sit, and would give an un-marked mistake no state to return
     * to. See `canMarkInvoicePaid` in `@wmf/shared/invoicing`.
     *
     * Null is outstanding, which is what the screen totals.
     */
    paidAt: timestamp("paid_at", { withTimezone: true }),
    paidByUserId: varchar("paid_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /** Whatever reconciles it against a bank statement — a BACS reference. */
    paymentReference: text("payment_reference"),

    notes: text("notes"),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("tutor_invoices_reference_idx").on(t.organisationId, t.reference),
    // "What do I still owe" is the question this feature is opened to answer,
    // so it gets an index rather than a sequential scan over every invoice
    // the school has ever issued.
    index("tutor_invoices_outstanding_idx")
      .on(t.organisationId, t.paidAt)
      .where(sql`paid_at is null and status <> 'void' and status <> 'draft'`),
    index("tutor_invoices_tutor_period_idx").on(t.tutorId, t.periodStart),
    index("tutor_invoices_org_status_idx").on(t.organisationId, t.status, t.periodEnd),
    check("tutor_invoices_period_order", sql`${t.periodEnd} >= ${t.periodStart}`),
    /**
     * One draft per tutor per period.
     *
     * Partial, and the partiality is the point: a schedule that fires twice,
     * or an owner who presses the button twice, must not produce two drafts
     * for the same fortnight — that is how somebody gets paid twice. A voided
     * invoice releases the period so it can be rebuilt, and a sent one does
     * too, deliberately: re-invoicing a period already sent is a real thing
     * (a lesson written up late), and it is visible in the list rather than
     * refused by an index.
     */
    uniqueIndex("tutor_invoices_open_period_idx")
      .on(t.tutorId, t.periodStart, t.periodEnd)
      .where(sql`status = 'draft'`),
  ],
);

/**
 * One lesson on an invoice, priced or explicitly not.
 *
 * **Excluded lines are stored too.** A Taster or a cancelled lesson is on the
 * document saying why it is not being charged for, because the document exists
 * to be agreed on and a lesson that silently vanished cannot be queried. The
 * counts on the invoice row are derived from these at build time and then
 * frozen with it.
 *
 * The appointment reference is `set null` rather than cascade: a lesson later
 * deleted from the mirror must not quietly remove a line from a document
 * somebody has already been paid against.
 */
export const tutorInvoiceLines = pgTable(
  "tutor_invoice_lines",
  {
    id: id(),
    invoiceId: varchar("invoice_id")
      .notNull()
      .references(() => tutorInvoices.id, { onDelete: "cascade" }),

    appointmentId: varchar("appointment_id").references(() => appointments.id, {
      onDelete: "set null",
    }),
    /** The provider's own id, kept as text so the line survives the above. */
    appointmentExternalId: text("appointment_external_id"),
    bookingId: varchar("booking_id").references(() => appointmentBookings.id, {
      onDelete: "set null",
    }),

    startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
    endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
    minutes: integer("minutes").notNull(),

    /** Copied, not joined: the document must read the same in a year. */
    studentName: text("student_name"),
    serviceName: text("service_name"),
    cancellationOutcome: text("cancellation_outcome"),
    cancellationActor: text("cancellation_actor"),
    cancellationFunding: text("cancellation_funding"),
    cancellationChoice: text("cancellation_choice"),
    cancellationLate: boolean("cancellation_late"),
    cancellationPaymentReceived: boolean("cancellation_payment_received"),

    cancellationWaived: boolean("cancellation_waived"),
    bookingStatus: text("booking_status").notNull(),
    attendanceStatus: text("attendance_status"),
    couponId: integer("coupon_id"),

    manualAmountPence: integer("manual_amount_pence"),
    ratePence: integer("rate_pence"),
    amountPence: integer("amount_pence").notNull().default(0),
    /** Null when charged; otherwise why not. See `ExclusionReason`. */
    excludedFor: text("excluded_for"),
    /** Null on an excluded line — nobody needs to have paid for it. */
    coverage: text("coverage"),

    createdAt: createdAt(),
  },
  (t) => [
    index("tutor_invoice_lines_invoice_idx").on(t.invoiceId, t.startsAt),
    index("tutor_invoice_lines_appointment_idx").on(t.appointmentId),
  ],
);

/**
 * Which bookings are already being paid for, and on which invoice.
 *
 * **The guard against paying for one Tuesday twice**, and it is a table rather
 * than a `where` clause because financial correctness must not depend on every
 * future caller remembering one. A date range is a *query*; the booking is the
 * thing being paid for, and two overlapping invoices must not both carry it.
 *
 * One row per charged line, written in the same transaction as the invoice.
 * The primary key on `booking_id` is the whole mechanism: two drafts built at
 * the same instant race for the row, one wins, and the loser fails its insert
 * rather than quietly producing a second payment for the same lesson.
 *
 * Rows are released when an invoice is voided, which is what makes a
 * correction possible — and are not released when it is sent, which is what
 * makes the guard mean anything.
 */
export const tutorInvoiceChargedBookings = pgTable(
  "tutor_invoice_charged_bookings",
  {
    /** The booking itself is the key. Deliberately not a surrogate id. */
    bookingId: varchar("booking_id")
      .primaryKey()
      .references(() => appointmentBookings.id, { onDelete: "cascade" }),
    organisationId: organisationId(),
    invoiceId: varchar("invoice_id")
      .notNull()
      .references(() => tutorInvoices.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [index("tutor_invoice_charged_bookings_invoice_idx").on(t.invoiceId)],
);

export const invoiceCadenceEnum = pgEnum("invoice_cadence", [
  "weekly",
  "fortnightly",
  "monthly",
]);

export type InvoiceCadenceValue = (typeof invoiceCadenceEnum.enumValues)[number];

/**
 * A standing instruction to build a draft every week, fortnight or month.
 *
 * **It drafts; it never sends.** The runner produces an invoice in `draft` and
 * stops there. Nothing in this system emails a tutor a claim about money
 * without a person having looked at it — the flags this feature exists to
 * raise (a rate nobody set, a family who has not paid) are exactly the things
 * an automated send would paper over.
 *
 * `nextPeriodStart` is where the next period begins, advanced as periods are
 * invoiced. Storing the frontier rather than a cron expression is what makes
 * the runner idempotent: a tick that runs twice, or a process that dies
 * halfway, cannot produce two invoices for one fortnight, because the second
 * attempt reads a frontier that has already moved.
 */
export const tutorInvoiceSchedules = pgTable(
  "tutor_invoice_schedules",
  {
    id: id(),
    organisationId: organisationId(),
    tutorId: varchar("tutor_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "cascade" }),

    cadence: invoiceCadenceEnum("cadence").notNull(),
    /** Inclusive start of the next period to invoice, in the school's timezone. */
    nextPeriodStart: date("next_period_start").notNull(),
    enabled: boolean("enabled").notNull().default(true),

    lastRunAt: timestamp("last_run_at", { withTimezone: true }),
    lastError: text("last_error"),

    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // One standing instruction per tutor. Two schedules for one person is a
    // way of invoicing the same Tuesday twice.
    uniqueIndex("tutor_invoice_schedules_tutor_idx").on(t.tutorId),
    index("tutor_invoice_schedules_due_idx").on(t.enabled, t.nextPeriodStart),
  ],
);

// ── School invoicing ─────────────────────────────────────────────────────────
//
// School teaching is billed to the family, not to the school. This is the
// whole of what the portal knows about that money, and it deliberately holds
// no card details, no payment provider and no bank connection: parents pay by
// transfer, so the portal's job is to say what is owed, give a reference to
// quote, and record that somebody has seen the money arrive.
//
// Amelia is not involved at any point. A private lesson's money lives in
// Amelia and is read from it; a school lesson's money lives here and is
// written by a person. Nothing joins the two, and nothing here should ever
// acquire an Amelia id in order to look more like the private side.

export const schoolInvoiceStatusEnum = pgEnum("school_invoice_status", [
  "draft",
  "issued",
  "paid",
  "void",
]);

/**
 * One bill to one family, covering whatever period an owner chose.
 *
 * **Addressed to the parent account rather than to a pupil**, because that is
 * who pays and how they pay: a family with two children at the school sends
 * one transfer, and two invoices with two references would produce exactly the
 * reconciliation problem bank transfer is prone to. The children appear as
 * lines.
 *
 * **The period is a date range rather than a term or a month.** Whether the
 * school invoices monthly, half-termly or per term is then a choice made when
 * raising one, not a shape baked into the table — and a one-off correction
 * covering three weeks needs no new concept.
 *
 * **The totals are stored, not recomputed.** An invoice is a statement made on
 * a date; if a rate changes afterwards, or a lesson is later cancelled, what
 * the family was asked for does not retrospectively change. The lines carry
 * their own amounts for the same reason.
 */
export const schoolInvoices = pgTable(
  "school_invoices",
  {
    id: id(),
    organisationId: organisationId(),
    /** The family this is addressed to. Deleting the account keeps the record. */
    parentUserId: varchar("parent_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "restrict" }),
    /**
     * What the family quotes on the transfer, and what an owner matches on the
     * bank statement. Unique per organisation and never reused, including by a
     * voided invoice — a reference that has been out in the world once must not
     * come back attached to a different amount.
     */
    reference: text("reference").notNull(),
    /** Inclusive start and end of the teaching this bill covers. */
    periodStart: date("period_start").notNull(),
    periodEnd: date("period_end").notNull(),
    /**
     * When the family should have paid by.
     *
     * Ordinarily before the period even starts: school teaching is invoiced in
     * advance, so a bill raised in August for the autumn term is due in
     * September and covers lessons into December. Nothing enforces it — no
     * late fee, no automatic chasing — it is what the family is told and what
     * an owner sorts the list by.
     */
    dueOn: date("due_on"),
    status: schoolInvoiceStatusEnum("status").notNull().default("draft"),
    /**
     * The sum of the lines, in pounds, as it stood when the bill was made.
     *
     * The gross: what the lessons on it came to. Credit applied is held
     * separately so the bill can show its own arithmetic — lessons, less
     * credit, to pay — rather than presenting a reduced figure with no
     * explanation of where it came from.
     */
    totalAmount: numeric("total_amount", { precision: 10, scale: 2 }).notNull(),
    /**
     * Credit taken off this bill at the moment it was issued.
     *
     * Written once, when the invoice is raised, and never afterwards: what a
     * family owes is `total_amount - credit_applied`, and a bill that changed
     * after it was sent would disagree with the copy in their inbox.
     */
    creditApplied: numeric("credit_applied", { precision: 10, scale: 2 })
      .notNull()
      .default("0"),
    notes: text("notes"),

    /** When the family was told. A draft has not been sent to anybody. */
    issuedAt: timestamp("issued_at", { withTimezone: true }),
    /**
     * When the family said they had paid — which is not the same as paid.
     *
     * Deliberately its own column rather than a status, because it is a claim
     * and `status = 'paid'` is a finding. A parent pressing "I've paid this"
     * tells the school to go and look at the bank; only an owner who has
     * looked sets `paid_at`, and only that puts a tick on the invoice. The two
     * facts coexist — a declared payment that turns out not to have arrived
     * keeps both the claim and its absence — and neither overwrites the other.
     *
     * Nothing here lets a family clear their own debt, which is the whole
     * reason it is not simply a status a parent may set.
     */
    parentDeclaredPaidAt: timestamp("parent_declared_paid_at", { withTimezone: true }),
    /** What the family said about it — "sent 14 Sep from J Hughes". */
    parentDeclaredNote: text("parent_declared_note"),
    /**
     * When somebody saw the money arrive, and who said so.
     *
     * A person rather than a bank feed, deliberately: there is no reconciliation
     * integration and pretending otherwise would make "paid" a guess. The
     * recorder is kept because "who marked this paid" is the first question
     * asked when a family says they paid and the bank says otherwise.
     */
    paidAt: timestamp("paid_at", { withTimezone: true }),
    paidByUserId: varchar("paid_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /** Free text for how it arrived — "transfer 14 Sep", "cash at the office". */
    paymentNote: text("payment_note"),

    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("school_invoices_reference_idx").on(t.organisationId, t.reference),
    index("school_invoices_parent_idx").on(t.organisationId, t.parentUserId, t.status),
    index("school_invoices_status_idx").on(t.organisationId, t.status, t.periodEnd),
    index("school_invoices_due_idx").on(t.organisationId, t.status, t.dueOn),
    check("school_invoices_period_order", sql`${t.periodEnd} >= ${t.periodStart}`),
    check("school_invoices_total_not_negative", sql`${t.totalAmount} >= 0`),
    /** Paid means somebody said when. The two are one fact, so they move together. */
    check(
      "school_invoices_paid_has_date",
      sql`(${t.status} <> 'paid') or (${t.paidAt} is not null)`,
    ),
  ],
);

/**
 * One school lesson on one invoice.
 *
 * The appointment is referenced rather than copied so the family can be shown
 * which lessons they are paying for and an owner can answer "why is it this
 * much" — but the description, date and amount are copied, because a bill has
 * to keep saying what it said even after a lesson is moved or a pupil is
 * renamed.
 *
 * A lesson appears on at most one invoice that is not void. That is the whole
 * defence against billing a family twice for the same Tuesday, and it is an
 * index rather than a check in the route, because two owners raising invoices
 * for overlapping ranges at the same moment is exactly the case a
 * read-then-write loses.
 */
export const schoolInvoiceLines = pgTable(
  "school_invoice_lines",
  {
    id: id(),
    organisationId: organisationId(),
    invoiceId: varchar("invoice_id")
      .notNull()
      .references(() => schoolInvoices.id, { onDelete: "cascade" }),
    /** Which child this line is for; a family invoice carries several. */
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "restrict" }),
    /** The lesson being charged for. Null once an appointment is hard-deleted. */
    appointmentId: varchar("appointment_id").references(() => appointments.id, {
      onDelete: "set null",
    }),
    schoolSessionId: varchar("school_session_id").references(() => schoolSessions.id, {
      onDelete: "restrict",
    }),
    /** Copied at issue: what the bill says, whatever happens to the lesson after. */
    studentName: text("student_name").notNull(),
    description: text("description").notNull(),
    lessonDate: timestamp("lesson_date", { withTimezone: true }).notNull(),
    amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
    /**
     * Whether the invoice this line sits on has been voided.
     *
     * A deliberate copy of the parent's status, and the only one. The rule it
     * exists for — a lesson is billed at most once — has to be an index, and a
     * partial index cannot reach the parent table to ask. Voiding writes both
     * in one transaction, which is the whole of the contract: nothing else
     * changes this column, and no read should prefer it to the invoice's own
     * status for any purpose but this index.
     */
    voided: boolean("voided").notNull().default(false),
    createdAt: createdAt(),
  },
  (t) => [
    index("school_invoice_lines_invoice_idx").on(t.invoiceId),
    index("school_invoice_lines_student_idx").on(t.organisationId, t.studentId),
    check("school_invoice_lines_amount_not_negative", sql`${t.amount} >= 0`),
    /**
     * One lesson, one bill.
     *
     * Partial, so that voiding an invoice releases its lessons to be billed
     * again — which is what voiding is for. Two owners raising invoices over
     * overlapping ranges at the same moment is exactly the race a
     * check-then-insert loses, so the database refuses the second rather than
     * the route trying to notice.
     */
    uniqueIndex("school_invoice_lines_once_idx")
      .on(t.organisationId, t.studentId, t.appointmentId)
      .where(sql`${t.voided} = false`),
    uniqueIndex("school_invoice_lines_session_once_idx")
      .on(t.organisationId, t.studentId, t.schoolSessionId)
      .where(sql`${t.voided} = false and ${t.schoolSessionId} is not null`),
  ],
);

/**
 * What a family is owed when a lesson they were billed for did not happen.
 *
 * School teaching is invoiced in advance, so this is ordinary rather than
 * exceptional: a term billed in August meets a snow day in January, and the
 * family have correctly paid for a lesson nobody taught.
 *
 * **Two ways to put it right, and the school chooses which.** A `make_up` is a
 * lesson owed — it is taught again and no money moves. A `money` credit is
 * held against the family and comes off their next invoice. Modelled as one
 * table with a kind rather than two, because they are the same fact — this
 * family is owed something for this lesson — settled two ways, and a make-up
 * that never happens converts to money without becoming a different record.
 *
 * **Nothing is refunded.** A money credit reduces the next bill; it is never
 * sent back. That is what keeps the portal out of moving money, and it is why
 * a credit is held against the family rather than subtracted from the invoice
 * that produced it — the bill that has already gone out never changes.
 */
export const schoolCreditKindEnum = pgEnum("school_credit_kind", ["make_up", "money"]);

export const schoolCreditStatusEnum = pgEnum("school_credit_status", [
  /** Owed, and not yet settled: a lesson to teach, or money to take off a bill. */
  "open",
  /** The make-up lesson was taught, or the money came off an invoice. */
  "settled",
  /** Written off by a person, with a reason. Never deleted. */
  "cancelled",
]);

export const schoolCredits = pgTable(
  "school_credits",
  {
    id: id(),
    organisationId: organisationId(),
    /**
     * The family who are owed, which is who an invoice is addressed to.
     *
     * Held against the household rather than the pupil because that is who the
     * credit is applied for — a family with two children has one bill, and a
     * credit earned by one child comes off it. Null keeps the credit on the
     * pupil until an owner deliberately chooses a verified billing family.
     */
    parentUserId: varchar("parent_user_id")
      .references(() => users.id, { onDelete: "restrict" }),
    /** Legacy billing identity; canonical-only pupils use schoolPupilId. */
    studentId: varchar("student_id")
      .references(() => students.id, { onDelete: "restrict" }),
    schoolPupilId: varchar("school_pupil_id").references(() => schoolPupils.id, { onDelete: "restrict" }),
    schoolSessionId: varchar("school_session_id").references(() => schoolSessions.id, { onDelete: "restrict" }),
    kind: schoolCreditKindEnum("kind").notNull(),
    status: schoolCreditStatusEnum("status").notNull().default("open"),
    /**
     * What the lesson was worth, in pounds.
     *
     * Recorded for a make-up too, so that converting one to money later needs
     * no second opinion about what it was worth — the rate may have changed by
     * then, and what the family paid is the honest figure.
     */
    amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
    /** Why, in words. Required: money coming off a bill must be accountable. */
    reason: text("reason").notNull(),

    /** The lesson that did not happen, where there is one. */
    appointmentId: varchar("appointment_id").references(() => appointments.id, {
      onDelete: "set null",
    }),
    /** The invoice that lesson was billed on, which never itself changes. */
    originInvoiceId: varchar("origin_invoice_id").references(() => schoolInvoices.id, {
      onDelete: "set null",
    }),
    /** The invoice a money credit came off. Null until it is applied. */
    appliedToInvoiceId: varchar("applied_to_invoice_id").references(() => schoolInvoices.id, {
      onDelete: "restrict",
    }),

    settledAt: timestamp("settled_at", { withTimezone: true }),
    settledByUserId: varchar("settled_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /** How it ended — "taught 3 Feb", or why it was written off. */
    settledNote: text("settled_note"),

    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    /** The working queue: who is owed what, per family. */
    index("school_credits_family_idx").on(t.organisationId, t.parentUserId, t.status),
    /** The roster: which children are owed a lesson. */
    index("school_credits_open_idx").on(t.organisationId, t.status, t.kind),
    index("school_credits_pupil_idx").on(t.organisationId, t.schoolPupilId, t.status),
    index("school_credits_student_idx").on(t.organisationId, t.studentId, t.status),
    index("school_credits_applied_idx").on(t.appliedToInvoiceId),
    check("school_credits_amount_positive", sql`${t.amount} > 0 or (${t.kind} = 'make_up' and ${t.amount} = 0)`),
    check("school_credits_pupil_present", sql`${t.studentId} is not null or ${t.schoolPupilId} is not null`),
    /**
     * Settled means somebody said when, and an applied credit names its
     * invoice. Both are one fact each, so they move together or not at all —
     * a credit marked settled with no date is one nobody can account for.
     */
    check(
      "school_credits_settled_has_date",
      sql`(${t.status} = 'open') or (${t.settledAt} is not null)`,
    ),
    check(
      "school_credits_applied_is_money",
      sql`(${t.appliedToInvoiceId} is null) or (${t.kind} = 'money' and ${t.status} = 'settled')`,
    ),
  ],
);

/** One retained decision per cancelled pupil/lesson. Credits have their own settlement lifecycle. */
export const schoolCancellations = pgTable("school_cancellations", {
  id: id(),
  organisationId: organisationId(),
  schoolKey: text("school_key").notNull(),
  schoolPupilId: varchar("school_pupil_id").references(() => schoolPupils.id, { onDelete: "restrict" }),
  studentId: varchar("student_id").references(() => students.id, { onDelete: "restrict" }),
  schoolSessionId: varchar("school_session_id").references(() => schoolSessions.id, { onDelete: "restrict" }),
  appointmentId: varchar("appointment_id").references(() => appointments.id, { onDelete: "restrict" }),
  pupilName: text("pupil_name").notNull(),
  startsAt: timestamp("starts_at", { withTimezone: true }).notNull(),
  endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
  serviceName: text("service_name"),
  resolution: text("resolution").notNull(),
  amount: numeric("amount", { precision: 10, scale: 2 }),
  reason: text("reason").notNull(),
  creditId: varchar("credit_id").references(() => schoolCredits.id, { onDelete: "restrict" }),
  originInvoiceId: varchar("origin_invoice_id").references(() => schoolInvoices.id, { onDelete: "restrict" }),
  createdByUserId: varchar("created_by_user_id").references(() => users.id, { onDelete: "restrict" }),
  createdAt: createdAt(),
}, (t) => [
  uniqueIndex("school_cancellations_session_pupil_idx").on(t.organisationId, t.schoolSessionId, t.schoolPupilId),
  uniqueIndex("school_cancellations_appointment_pupil_idx").on(t.organisationId, t.appointmentId, t.studentId),
  index("school_cancellations_pupil_idx").on(t.organisationId, t.schoolPupilId, t.createdAt),
  index("school_cancellations_student_idx").on(t.organisationId, t.studentId, t.createdAt),
  check("school_cancellations_resolution", sql`${t.resolution} in ('make_up', 'money', 'no_credit')`),
  check("school_cancellations_identity", sql`(${t.schoolSessionId} is not null and ${t.schoolPupilId} is not null and ${t.appointmentId} is null) or (${t.appointmentId} is not null and ${t.studentId} is not null and ${t.schoolSessionId} is null)`),
]);

/** Idempotent family-created records; names never identify someone else's child. */
export const familyLearnerCreations = pgTable("family_learner_creations", {
  id: id(), organisationId: organisationId(),
  userId: varchar("user_id").notNull().references(() => users.id),
  requestKey: varchar("request_key").notNull(), fullName: text("full_name").notNull(),
  isUnder18: boolean("is_under_18").notNull(),
  studentId: varchar("student_id").notNull().references(() => students.id),
  createdAt: createdAt(),
}, t => [uniqueIndex("family_learner_creations_request_idx").on(t.organisationId, t.userId, t.requestKey)]);
